import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import {
  Box,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  Slider,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import LinkIcon from '@mui/icons-material/Link';
import AuthContext from '../../context/authContext';
import useCompetitorAvailability from '../../hooks/useCompetitorAvailability';
import useEntertainment from '../../hooks/useEntertainment';
import useStatisticJob from '../../hooks/useStatisticJob';
import useStatisticCacheEntries from '../../hooks/useStatisticCacheEntries';
import StatisticViewShell from '../ui/StatisticViewShell';
import {
  getCartesianGridStroke,
  getRechartsAxisStroke,
  getRechartsTooltipContentStyle,
  getStatisticsAltSurfaceBackground,
  getStatisticsDeepSurfaceBackground,
  getStatisticsPlainAccordionSx,
  getStatisticsSurfaceBackground,
  getStatisticsTooltipBoxSx,
} from '../../util/chartTheme';
import { currencyFormatter, percentFormatter } from '../../util/formatHelper';
import { STATISTIC_TYPE_COMPETITOR_AVAILABILITY } from '../../constants/statisticTypes';
import EntertainmentCapacity from '../Bookings/components/EntertainmentCapacity';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ReferenceArea,
  ReferenceLine,
} from 'recharts';

const defaultStartDate = '2025-10-01';
const defaultEndDate = dayjs().endOf('month').format('YYYY-MM-DD');

const formatCurrency = value => currencyFormatter.format(value ?? 0);

const formatGermanDate = value => {
  const s = String(value || '').trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return s;
  const [, yyyy, mm, dd] = m;
  return `${dd}.${mm}.${yyyy}`;
};

const formatGermanDateTime = value => {
  const parsed = dayjs(value);
  if (!parsed.isValid()) return 'Kein Zeitstempel';
  return parsed.format('DD.MM.YYYY HH:mm');
};

const formatAgeLabel = value => {
  const hours = Number(value);
  if (!Number.isFinite(hours) || hours < 0) return 'Kein Alter';
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} Min.`;
  if (hours < 10) return `${hours.toFixed(1)} Std.`;
  if (hours < 48) return `${Math.round(hours)} Std.`;
  const days = hours / 24;
  if (days < 10) return `${days.toFixed(1)} Tg.`;
  return `${Math.round(days)} Tg.`;
};

const AGE_BUCKETS = [
  { key: 'lt1h', label: '0-1 Std.', maxHours: 1 },
  { key: 'lt6h', label: '1-6 Std.', maxHours: 6 },
  { key: 'lt24h', label: '6-24 Std.', maxHours: 24 },
  { key: 'lt72h', label: '1-3 Tage', maxHours: 72 },
  { key: 'lt168h', label: '3-7 Tage', maxHours: 168 },
  { key: 'gte168h', label: '> 7 Tage', maxHours: Number.POSITIVE_INFINITY },
];

const HISTORICAL_TARGET_BUCKET = {
  key: 'historicalTarget',
  label: 'Historische Zieltage',
};

const getAgeBucketKey = ageHours => {
  const hours = Number(ageHours);
  if (!Number.isFinite(hours) || hours < 0) return AGE_BUCKETS[AGE_BUCKETS.length - 1].key;
  return AGE_BUCKETS.find(bucket => hours <= bucket.maxHours)?.key || AGE_BUCKETS[AGE_BUCKETS.length - 1].key;
};

const isHistoricalTargetDate = value => {
  const parsed = dayjs(value);
  if (!parsed.isValid()) return false;
  return parsed.endOf('day').isBefore(dayjs());
};

const getRelativeTimeLabel = value => {
  const parsed = dayjs(value);
  if (!parsed.isValid()) return 'Kein Zeitstempel';
  const minutes = Math.max(0, dayjs().diff(parsed, 'minute'));
  if (minutes <= 1) return 'gerade eben';
  if (minutes < 60) return `vor ${minutes} Min.`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `vor ${hours} Std.`;
  const days = Math.round(minutes / 1440);
  return `vor ${days} Tg.`;
};

const buildFreshnessMeta = (theme, value) => {
  const isLight = theme.palette.mode === 'light';
  const neutralBorder = isLight ? theme.palette.divider : alpha(theme.palette.common.white, 0.12);
  const neutralBg = isLight ? theme.palette.grey[100] : alpha(theme.palette.common.white, 0.03);
  const parsed = dayjs(value);
  if (!parsed.isValid()) {
    return {
      label: 'Kein Zeitstempel',
      ageLabel: 'Kein Zeitstempel',
      chipColor: 'default',
      borderColor: neutralBorder,
      backgroundColor: neutralBg,
    };
  }

  const minutes = Math.max(0, dayjs().diff(parsed, 'minute'));
  if (minutes <= 120) {
    return {
      label: 'Sehr aktuell',
      ageLabel: getRelativeTimeLabel(parsed),
      chipColor: 'success',
      borderColor: 'rgba(76,175,80,0.45)',
      backgroundColor: 'rgba(76,175,80,0.10)',
    };
  }
  if (minutes <= 24 * 60) {
    return {
      label: 'Heute aktualisiert',
      ageLabel: getRelativeTimeLabel(parsed),
      chipColor: 'success',
      borderColor: 'rgba(76,175,80,0.25)',
      backgroundColor: 'rgba(76,175,80,0.05)',
    };
  }
  if (minutes <= 3 * 24 * 60) {
    return {
      label: 'Etwas älter',
      ageLabel: getRelativeTimeLabel(parsed),
      chipColor: 'warning',
      borderColor: 'rgba(255,152,0,0.35)',
      backgroundColor: 'rgba(255,152,0,0.06)',
    };
  }
  return {
    label: 'Veraltet',
    ageLabel: getRelativeTimeLabel(parsed),
    chipColor: 'error',
    borderColor: 'rgba(244,67,54,0.40)',
    backgroundColor: 'rgba(244,67,54,0.08)',
  };
};

const WEEKDAYS = [
  { key: 1, label: 'Mo' },
  { key: 2, label: 'Di' },
  { key: 3, label: 'Mi' },
  { key: 4, label: 'Do' },
  { key: 5, label: 'Fr' },
  { key: 6, label: 'Sa' },
  { key: 0, label: 'So' },
];

const addSoftHyphens = value => {
  const s = String(value ?? '');
  return s
    .replaceAll('_', '_\u00AD')
    .replaceAll('/', '/\u00AD')
    .replaceAll(':', ':\u00AD')
    .replaceAll('(', '(\u00AD')
    .replaceAll(')', '\u00AD)');
};

const normalizeEntertainmentTypeKey = value =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');

const getCompetitorEntertainmentTypeKeys = value => {
  const base = normalizeEntertainmentTypeKey(value);
  if (!base) return [];
  const keys = new Set([base]);
  if (base.endsWith('tag') && base.length > 3) keys.add(base.slice(0, -3)); // e.g. "lasertag" -> "laser"
  if (base.endsWith('zone') && base.length > 4) keys.add(base.slice(0, -4)); // e.g. "laserzone" -> "laser"
  return Array.from(keys);
};

const getEntertainmentTypeMatchKeys = entertainment => {
  const keys = new Set();
  const nameKey = normalizeEntertainmentTypeKey(entertainment?.entertainmentName);
  if (nameKey) {
    keys.add(nameKey);
    if (nameKey.endsWith('zone') && nameKey.length > 4) keys.add(nameKey.slice(0, -4));
  }
  const longKey = normalizeEntertainmentTypeKey(entertainment?.longName);
  if (longKey) keys.add(longKey);
  return keys;
};

const entertainmentMatchesCompetitorType = (entertainment, competitorType) => {
  const competitorKeys = getCompetitorEntertainmentTypeKeys(competitorType);
  if (!competitorKeys.length) return false;
  const entKeys = getEntertainmentTypeMatchKeys(entertainment);
  if (!entKeys.size) return false;
  return competitorKeys.some(k => entKeys.has(k));
};

const roundToTwoSafe = value => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
};

const formatDecimalForCsv = value => {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  let s = String(n);
  if (s.includes('e') || s.includes('E')) {
    s = n.toFixed(8).replace(/0+$/, '').replace(/\.$/, '');
  }
  return s.replace('.', ',');
};

const csvEscape = value => {
  if (value === null || value === undefined) return '';
  const s = String(value);
  if (!/[;"\r\n]/.test(s)) return s;
  return `"${s.replaceAll('"', '""')}"`;
};

const downloadTextFile = ({ filename, text, mimeType = 'text/plain;charset=utf-8' }) => {
  const blob = new Blob([text], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const clampNumber = (value, min, max) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null; // allow empty input while typing
    // allow typing in-progress numbers without committing yet
    if (
      trimmed === '.' ||
      trimmed === ',' ||
      trimmed === '-' ||
      trimmed === '-.' ||
      trimmed === '-,' ||
      trimmed.endsWith('.') ||
      trimmed.endsWith(',')
    ) {
      return null;
    }
  }
  const n = Number(value);
  if (Number.isNaN(n)) return null;
  return Math.min(max, Math.max(min, n));
};

const CompetitorAvailability = () => {
  const theme = useTheme();
  const statSurfaceBg = useMemo(() => getStatisticsSurfaceBackground(theme), [theme]);
  const statAltSurfaceBg = useMemo(() => getStatisticsAltSurfaceBackground(theme), [theme]);
  const statDeepSurfaceBg = useMemo(() => getStatisticsDeepSurfaceBackground(theme), [theme]);
  const chartGridStroke = useMemo(() => getCartesianGridStroke(theme), [theme]);
  const chartAxisStroke = useMemo(() => getRechartsAxisStroke(theme), [theme]);
  const chartTooltipContentStyle = useMemo(() => getRechartsTooltipContentStyle(theme), [theme]);
  const competitorTooltipBoxSx = useMemo(() => getStatisticsTooltipBoxSx(theme), [theme]);
  const plainAccordionSx = useMemo(() => getStatisticsPlainAccordionSx(theme), [theme]);
  const chartRefAreaFill = useMemo(
    () => (theme.palette.mode === 'light' ? theme.palette.common.black : theme.palette.common.white),
    [theme]
  );
  const chartRefLineStroke = useMemo(() => theme.palette.grey[500], [theme]);
  const chartRefLabelFill = useMemo(() => theme.palette.text.secondary, [theme]);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const authCtx = useContext(AuthContext);

  const [competitors, setCompetitors] = useState([]);
  const [appliedFilter, setAppliedFilter] = useState({
    startDate: defaultStartDate,
    endDate: defaultEndDate,
    competitors: [],
    benchmarkEntertainmentIds: [],
  });

  const [startDate, setStartDate] = useState(dayjs(defaultStartDate));
  const [endDate, setEndDate] = useState(dayjs(defaultEndDate));
  const [selectedCompetitors, setSelectedCompetitors] = useState([]);
  const [selectedBenchmarkEntertainmentIds, setSelectedBenchmarkEntertainmentIds] = useState([]);
  const [didManuallySelectBenchmarkEntertainmentIds, setDidManuallySelectBenchmarkEntertainmentIds] =
    useState(false);
  const [allEntertainments, setAllEntertainments] = useState([]);
  const [isBenchmarkDefaultsModalOpen, setIsBenchmarkDefaultsModalOpen] = useState(false);
  const [benchmarkDefaultsCompetitorKey, setBenchmarkDefaultsCompetitorKey] = useState('');
  const [benchmarkDefaultsEntertainmentIds, setBenchmarkDefaultsEntertainmentIds] = useState([]);
  const [isSavingBenchmarkDefaults, setIsSavingBenchmarkDefaults] = useState(false);

  const [gamesPerPlayer, setGamesPerPlayer] = useState(3.5);
  const [revenuePerPlayer, setRevenuePerPlayer] = useState(27);

  const [gamesPerPlayerInput, setGamesPerPlayerInput] = useState(String(3.5));
  const [revenuePerPlayerInput, setRevenuePerPlayerInput] = useState(String(27));
  const [isGamesPerPlayerFocused, setIsGamesPerPlayerFocused] = useState(false);
  const [isRevenuePerPlayerFocused, setIsRevenuePerPlayerFocused] = useState(false);
  const [expandedWeekdayHourlyGroups, setExpandedWeekdayHourlyGroups] = useState(() => new Set());
  const [expandedWeekdayTimelineGroups, setExpandedWeekdayTimelineGroups] = useState(() => new Set());
  const [expandedRevenueDistGroups, setExpandedRevenueDistGroups] = useState(() => new Set());
  const [expandedRevenueWeekdayHourlyGroups, setExpandedRevenueWeekdayHourlyGroups] = useState(() => new Set());
  const [expandedGuestsDistGroups, setExpandedGuestsDistGroups] = useState(() => new Set());
  const [expandedGuestsWeekdayHourlyGroups, setExpandedGuestsWeekdayHourlyGroups] = useState(() => new Set());
  const [assumptionsByCompetitor, setAssumptionsByCompetitor] = useState({});
  const competitorAssumptionDraftsRef = useRef({});
  const [selectedCacheEntryId, setSelectedCacheEntryId] = useState(null);

  useEffect(() => {
    if (isGamesPerPlayerFocused) return;
    setGamesPerPlayerInput(String(Number(gamesPerPlayer).toFixed(1)));
  }, [gamesPerPlayer, isGamesPerPlayerFocused]);

  useEffect(() => {
    if (isRevenuePerPlayerFocused) return;
    setRevenuePerPlayerInput(String(Number(revenuePerPlayer).toFixed(2)));
  }, [revenuePerPlayer, isRevenuePerPlayerFocused]);

  useEffect(() => {
    if (!Array.isArray(selectedCompetitors) || selectedCompetitors.length <= 1) return;
    setAssumptionsByCompetitor(prev => {
      const next = { ...(prev || {}) };
      selectedCompetitors.forEach(k => {
        const key = String(k || '').trim();
        if (!key) return;
        if (next[key]) return;
        next[key] = {
          gamesPerPlayer,
          revenuePerPlayer,
          gamesPerPlayerInput: String(Number(gamesPerPlayer).toFixed(1)),
          revenuePerPlayerInput: String(Number(revenuePerPlayer).toFixed(2)),
          isGamesFocused: false,
          isRevenueFocused: false,
        };
      });
      return next;
    });
  }, [selectedCompetitors, gamesPerPlayer, revenuePerPlayer]);

  const applyGlobalAssumptionsToSelectedCompetitors = useCallback(
    ({ nextGamesPerPlayer, nextRevenuePerPlayer }) => {
      const gp = Number(nextGamesPerPlayer);
      const rp = Number(nextRevenuePerPlayer);
      if (!Number.isFinite(gp) || !Number.isFinite(rp)) return;
      if (!Array.isArray(selectedCompetitors) || selectedCompetitors.length === 0) return;

      setAssumptionsByCompetitor(prev => {
        const next = { ...(prev || {}) };
        selectedCompetitors.forEach(k => {
          const key = String(k || '').trim();
          if (!key) return;
          const entry = next[key] || {};
          next[key] = {
            ...entry,
            gamesPerPlayer: gp,
            revenuePerPlayer: rp,
            gamesPerPlayerInput: String(gp.toFixed(1)),
            revenuePerPlayerInput: String(rp.toFixed(2)),
            isGamesFocused: false,
            isRevenueFocused: false,
          };
        });
        return next;
      });
    },
    [selectedCompetitors]
  );

  const {
    getCompetitors,
    updateCompetitorDefaults,
  } = useCompetitorAvailability();
  const { getEntertainments } = useEntertainment();
  const getCompetitorsRef = useRef(getCompetitors);
  const updateCompetitorDefaultsRef = useRef(updateCompetitorDefaults);
  const getEntertainmentsRef = useRef(getEntertainments);

  useEffect(() => {
    getCompetitorsRef.current = getCompetitors;
  }, [getCompetitors]);
  useEffect(() => {
    updateCompetitorDefaultsRef.current = updateCompetitorDefaults;
  }, [updateCompetitorDefaults]);
  useEffect(() => {
    getEntertainmentsRef.current = getEntertainments;
  }, [getEntertainments]);

  useEffect(() => {
    const loadCompetitors = async () => {
      try {
        const list = await getCompetitorsRef.current();
        setCompetitors(Array.isArray(list) ? list : []);
      } catch (e) {
        toast.error('Fehler beim Laden der Konkurrenten');
      }
    };
    loadCompetitors();
  }, []);

  useEffect(() => {
    const loadEntertainments = async () => {
      try {
        const list = await getEntertainmentsRef.current(true);
        setAllEntertainments(Array.isArray(list) ? list : []);
      } catch (_) {
        setAllEntertainments([]);
      }
    };
    loadEntertainments();
  }, []);

  const competitorsByKey = useMemo(() => {
    const map = new Map();
    (competitors || []).forEach(c => {
      if (c?.competitorKey) map.set(c.competitorKey, c);
    });
    return map;
  }, [competitors]);

  const entertainmentsById = useMemo(() => {
    const map = new Map();
    (allEntertainments || []).forEach(e => {
      const id = String(e?._id || '').trim();
      if (!id) return;
      map.set(id, e);
    });
    return map;
  }, [allEntertainments]);

  const benchmarkTypeKeysForSelection = useMemo(() => {
    const keys = new Set();
    (selectedCompetitors || []).forEach(k => {
      const c = competitorsByKey.get(k);
      const t = String(c?.entertainmentType || '').trim();
      if (t) keys.add(t);
    });
    return Array.from(keys);
  }, [selectedCompetitors, competitorsByKey]);

  const defaultBenchmarkEntertainmentIdsForSelection = useMemo(() => {
    const ids = [];
    (selectedCompetitors || []).forEach(k => {
      const c = competitorsByKey.get(k);
      const arr = Array.isArray(c?.benchmarkEntertainmentIds) ? c.benchmarkEntertainmentIds : [];
      const legacy = c?.entertainmentId ? [String(c.entertainmentId)] : [];
      [...arr, ...legacy].forEach(v => {
        const s = String(v || '').trim();
        if (s) ids.push(s);
      });
    });
    return Array.from(new Set(ids));
  }, [selectedCompetitors, competitorsByKey]);

  useEffect(() => {
    if (didManuallySelectBenchmarkEntertainmentIds) return;
    setSelectedBenchmarkEntertainmentIds(defaultBenchmarkEntertainmentIdsForSelection);
  }, [defaultBenchmarkEntertainmentIdsForSelection, didManuallySelectBenchmarkEntertainmentIds]);

  const benchmarkOptionsByType = useMemo(() => {
    const allowedKeys = new Set((benchmarkTypeKeysForSelection || []).flatMap(getCompetitorEntertainmentTypeKeys));
    if (!allowedKeys.size) return [];

    const grouped = new Map();
    (allEntertainments || []).forEach(e => {
      const typeLabel = String(e?.entertainmentName || '').trim();
      if (!typeLabel) return;
      const entKeys = getEntertainmentTypeMatchKeys(e);
      let matches = false;
      for (const k of allowedKeys) {
        if (entKeys.has(k)) {
          matches = true;
          break;
        }
      }
      if (!matches) return;
      const list = grouped.get(typeLabel) || [];
      list.push(e);
      grouped.set(typeLabel, list);
    });

    const result = Array.from(grouped.entries())
      .map(([type, list]) => ({
        type,
        items: (list || []).slice().sort((a, b) => String(a?.longName || '').localeCompare(String(b?.longName || ''))),
      }))
      .sort((a, b) => String(a.type).localeCompare(String(b.type)));

    if (result.length) return result;

    // Fallback: allow manual assignment even if the competitor type keys don't match our entertainment names.
    const allGrouped = new Map();
    (allEntertainments || []).forEach(e => {
      const typeLabel = String(e?.entertainmentName || '').trim();
      if (!typeLabel) return;
      const list = allGrouped.get(typeLabel) || [];
      list.push(e);
      allGrouped.set(typeLabel, list);
    });
    return Array.from(allGrouped.entries())
      .map(([type, list]) => ({
        type,
        items: (list || []).slice().sort((a, b) => String(a?.longName || '').localeCompare(String(b?.longName || ''))),
      }))
      .sort((a, b) => String(a.type).localeCompare(String(b.type)));
  }, [allEntertainments, benchmarkTypeKeysForSelection]);

  const openBenchmarkDefaultsModal = useCallback(() => {
    const initialKey =
      selectedCompetitors.length === 1 ? String(selectedCompetitors[0] || '').trim() : '';
    setBenchmarkDefaultsCompetitorKey(initialKey);

    const initialCompetitor = initialKey ? competitorsByKey.get(initialKey) : null;
    const initialIds = initialCompetitor
      ? Array.from(
          new Set(
            [
              ...(Array.isArray(initialCompetitor?.benchmarkEntertainmentIds)
                ? initialCompetitor.benchmarkEntertainmentIds
                : []),
              ...(initialCompetitor?.entertainmentId ? [String(initialCompetitor.entertainmentId)] : []),
            ]
              .map(v => String(v || '').trim())
              .filter(Boolean)
          )
        )
      : [];
    setBenchmarkDefaultsEntertainmentIds(initialIds);
    setIsBenchmarkDefaultsModalOpen(true);
  }, [selectedCompetitors, competitorsByKey]);

  const closeBenchmarkDefaultsModal = useCallback(() => {
    if (isSavingBenchmarkDefaults) return;
    setIsBenchmarkDefaultsModalOpen(false);
  }, [isSavingBenchmarkDefaults]);

  const benchmarkDefaultsEntertainmentOptions = useMemo(() => {
    const key = String(benchmarkDefaultsCompetitorKey || '').trim();
    const c = key ? competitorsByKey.get(key) : null;
    const type = String(c?.entertainmentType || '').trim();
    const sortedAll = (allEntertainments || [])
      .slice()
      .sort((a, b) => String(a?.longName || '').localeCompare(String(b?.longName || '')));
    if (!type) return sortedAll;

    const matches = (allEntertainments || [])
      .filter(e => entertainmentMatchesCompetitorType(e, type))
      .slice()
      .sort((a, b) => String(a?.longName || '').localeCompare(String(b?.longName || '')));

    return matches.length ? matches : sortedAll;
  }, [benchmarkDefaultsCompetitorKey, competitorsByKey, allEntertainments]);

  const saveBenchmarkDefaults = useCallback(async () => {
    const competitorKey = String(benchmarkDefaultsCompetitorKey || '').trim();
    if (!competitorKey) {
      toast.error('Bitte wählen Sie einen Konkurrenten aus');
      return;
    }
    const ids = Array.from(
      new Set((benchmarkDefaultsEntertainmentIds || []).map(v => String(v || '').trim()).filter(Boolean))
    );
    if (!ids.length) {
      toast.error('Bitte wählen Sie mindestens ein FunZone-Entertainment aus');
      return;
    }
    if (isSavingBenchmarkDefaults) return;

    setIsSavingBenchmarkDefaults(true);
    try {
      const competitor = competitorsByKey.get(competitorKey);
      const entertainmentType = String(competitor?.entertainmentType || '').trim() || undefined;
      await updateCompetitorDefaultsRef.current({
        competitorKey,
        benchmarkEntertainmentIds: ids,
        entertainmentType,
      });
      const list = await getCompetitorsRef.current();
      setCompetitors(Array.isArray(list) ? list : []);
      toast.success('Standard-Zuordnung gespeichert');
      setIsBenchmarkDefaultsModalOpen(false);
    } catch (_) {
      toast.error('Fehler beim Speichern der Standard-Zuordnung');
    } finally {
      setIsSavingBenchmarkDefaults(false);
    }
  }, [benchmarkDefaultsCompetitorKey, benchmarkDefaultsEntertainmentIds, isSavingBenchmarkDefaults, competitorsByKey]);

  const [didAutoSelectFirstCompetitor, setDidAutoSelectFirstCompetitor] = useState(false);

  // Auto-select first competitor and auto-apply once competitors are loaded.
  useEffect(() => {
    if (didAutoSelectFirstCompetitor) return;
    if (!competitors?.length) return;
    if (selectedCompetitors?.length) return;

    const first = competitors.find(c => c?.competitorKey) || null;
    if (!first) return;

    const firstKey = first.competitorKey;
    setDidAutoSelectFirstCompetitor(true);
    setSelectedCompetitors([firstKey]);

    const defaultBenchmarkIds =
      Array.isArray(first?.benchmarkEntertainmentIds) && first.benchmarkEntertainmentIds.length
        ? first.benchmarkEntertainmentIds.map(String)
        : first?.entertainmentId
          ? [String(first.entertainmentId)]
          : [];
    setSelectedBenchmarkEntertainmentIds(defaultBenchmarkIds);
    setDidManuallySelectBenchmarkEntertainmentIds(false);

    setAppliedFilter(prev => ({
      ...prev,
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
      competitors: [firstKey],
      benchmarkEntertainmentIds: defaultBenchmarkIds,
    }));
  }, [competitors, selectedCompetitors, didAutoSelectFirstCompetitor, startDate, endDate]);

  const filterParams = useMemo(() => {
    const competitorsCsv = Array.from(new Set(appliedFilter.competitors || [])).sort().join(',');
    const benchmarkCsv = Array.from(new Set(appliedFilter.benchmarkEntertainmentIds || [])).sort().join(',');
    if (!appliedFilter.startDate || !appliedFilter.endDate || !competitorsCsv) return null;
    return {
      startDate: appliedFilter.startDate,
      endDate: appliedFilter.endDate,
      competitors: competitorsCsv,
      benchmarkEntertainmentIds: benchmarkCsv,
    };
  }, [appliedFilter]);

  const {
    payload: statisticPayload,
    isStale,
    cacheComputedAt,
    cacheExpiresAt,
    status: jobStatus,
    job,
    error: jobError,
    loadCacheEntry,
    refresh: refreshStatistic,
  } = useStatisticJob({
    statisticType: STATISTIC_TYPE_COMPETITOR_AVAILABILITY,
    endpoint: '/api/v1/competitorAvailability/statistics',
    filterParams,
  });

  const { entries: cacheEntries, isLoading: entriesLoading } = useStatisticCacheEntries({
    statisticType: STATISTIC_TYPE_COMPETITOR_AVAILABILITY,
  });

  const isFetchingStats =
    jobStatus === 'fetching' || jobStatus === 'pending' || jobStatus === 'running';

  useEffect(() => {
    if (jobStatus === 'failed' && jobError) toast.error(jobError);
  }, [jobStatus, jobError]);

  const handleSelectCacheEntry = useCallback(
    entryId => {
      setSelectedCacheEntryId(entryId);
      if (!entryId) return;
      loadCacheEntry(entryId);

      const entry = (cacheEntries || []).find(e => String(e?._id) === String(entryId));
      const fp = entry?.filterParams || {};
      if (fp.startDate) {
        const d = dayjs(fp.startDate);
        if (d.isValid()) setStartDate(d);
      }
      if (fp.endDate) {
        const d = dayjs(fp.endDate);
        if (d.isValid()) setEndDate(d);
      }
      if (typeof fp.competitors === 'string') {
        const arr = fp.competitors.split(',').map(s => s.trim()).filter(Boolean);
        setSelectedCompetitors(arr);
      }
      if (typeof fp.benchmarkEntertainmentIds === 'string') {
        const arr = fp.benchmarkEntertainmentIds.split(',').map(s => s.trim()).filter(Boolean);
        setSelectedBenchmarkEntertainmentIds(arr);
        setDidManuallySelectBenchmarkEntertainmentIds(true);
      }

      setAppliedFilter(prev => ({
        startDate: String(fp.startDate || prev.startDate),
        endDate: String(fp.endDate || prev.endDate),
        competitors:
          typeof fp.competitors === 'string'
            ? fp.competitors.split(',').map(s => s.trim()).filter(Boolean)
            : prev.competitors,
        benchmarkEntertainmentIds:
          typeof fp.benchmarkEntertainmentIds === 'string'
            ? fp.benchmarkEntertainmentIds.split(',').map(s => s.trim()).filter(Boolean)
            : prev.benchmarkEntertainmentIds,
      }));
    },
    [cacheEntries, loadCacheEntry]
  );

  const applyFilters = (nextStart, nextEnd) => {
    const s = nextStart || startDate;
    const e = nextEnd || endDate;
    if (!s || !e || !s.isValid() || !e.isValid()) {
      toast.error('Bitte wählen Sie gültige Daten aus');
      return;
    }
    if (s.isAfter(e)) {
      toast.error('Das Startdatum darf nicht nach dem Enddatum liegen');
      return;
    }
    if (!selectedCompetitors.length) {
      toast.error('Bitte wählen Sie mindestens einen Konkurrenten aus');
      return;
    }
    const nextBenchmarkEntertainmentIds = Array.from(
      new Set((selectedBenchmarkEntertainmentIds || []).map(v => String(v || '').trim()).filter(Boolean))
    );

    setSelectedCacheEntryId(null);
    setAppliedFilter({
      startDate: s.format('YYYY-MM-DD'),
      endDate: e.format('YYYY-MM-DD'),
      competitors: selectedCompetitors,
      benchmarkEntertainmentIds: nextBenchmarkEntertainmentIds,
    });
  };

  useEffect(() => {
    setDidManuallySelectBenchmarkEntertainmentIds(false);
  }, [selectedCompetitors]);

  const stats = statisticPayload;
  const ownStats = useMemo(
    () => (statisticPayload ? { entertainments: statisticPayload.ownEntertainments || [] } : null),
    [statisticPayload]
  );

  const competitorResults = useMemo(() => {
    return Array.isArray(stats?.competitors) ? stats.competitors : [];
  }, [stats]);

  const pendantResults = useMemo(() => {
    return Array.isArray(ownStats?.entertainments) ? ownStats.entertainments : [];
  }, [ownStats]);

  const pendantsById = useMemo(() => {
    const map = new Map();
    pendantResults.forEach(p => {
      const id = String(p?.entertainmentId || '').trim();
      if (!id) return;
      map.set(id, p);
    });
    return map;
  }, [pendantResults]);

  const timeSeriesData = useMemo(() => {
    if (!competitorResults.length && !pendantResults.length) return [];
    const multiCompetitorMode = (appliedFilter?.competitors || []).length > 1;

    const allDates = new Set();
    competitorResults.forEach(c => (c?.timeSeries?.daily || []).forEach(d => allDates.add(d.date)));
    pendantResults.forEach(p => (p?.timeSeries?.daily || []).forEach(d => allDates.add(d.date)));

    const sortedDates = Array.from(allDates).sort((a, b) => String(a).localeCompare(String(b)));

    const byCompetitor = new Map();
    competitorResults.forEach(c => {
      const map = new Map();
      (c?.timeSeries?.daily || []).forEach(d => map.set(d.date, d));
      byCompetitor.set(c.competitorKey, map);
    });

    const byPendant = new Map();
    pendantResults.forEach(p => {
      const map = new Map();
      (p?.timeSeries?.daily || []).forEach(d => map.set(d.date, d));
      byPendant.set(p.entertainmentId, map);
    });

    const rows = sortedDates.map(date => {
      const row = { date, label: dayjs(date).format('DD.MM.') };
      const freshnessItems = [];
      competitorResults.forEach(c => {
        const daily = byCompetitor.get(c.competitorKey)?.get(date);
        row[`${c.competitorKey}__util`] = daily?.utilizationPercent ?? 0;
        const bookedSlots = daily?.bookedSlots ?? 0;
        const perC = assumptionsByCompetitor?.[c.competitorKey];
        const gp = multiCompetitorMode ? Number(perC?.gamesPerPlayer ?? gamesPerPlayer) : gamesPerPlayer;
        const rp = multiCompetitorMode ? Number(perC?.revenuePerPlayer ?? revenuePerPlayer) : revenuePerPlayer;
        const guests = gp > 0 ? bookedSlots / gp : 0;
        row[`${c.competitorKey}__rev`] = guests * rp;
        if (daily?.scrapedAt) {
          freshnessItems.push({
            entityKey: c.competitorKey,
            label: c.competitorName || c.competitorKey,
            targetDate: date,
            scrapedAt: daily.scrapedAt,
          });
        }
      });
      pendantResults.forEach(p => {
        const daily = byPendant.get(p.entertainmentId)?.get(date);
        const key = `funzone_${p.entertainmentId}`;
        row[`${key}__util`] = daily?.utilizationPercent ?? 0;
        // FunZone revenue is based on real booking prices (backend), not assumptions.
        row[`${key}__rev`] = daily?.revenue ?? 0;
      });
      row.__freshnessItems = freshnessItems;
      return row;
    });

    const smoothKeys = [];
    competitorResults.forEach(c => {
      smoothKeys.push(`${c.competitorKey}__util`, `${c.competitorKey}__rev`);
    });
    pendantResults.forEach(p => {
      smoothKeys.push(`funzone_${p.entertainmentId}__util`, `funzone_${p.entertainmentId}__rev`);
    });

    if (rows.length >= 2 && smoothKeys.length > 0) {
      const SMOOTHING_WINDOW = 7;
      const halfWindow = Math.floor(SMOOTHING_WINDOW / 2);
      for (const key of smoothKeys) {
        const original = rows.map(r => Number(r[key]) || 0);
        for (let i = 0; i < rows.length; i++) {
          const start = Math.max(0, i - halfWindow);
          const end = Math.min(rows.length - 1, i + halfWindow);
          let sum = 0;
          let count = 0;
          for (let j = start; j <= end; j++) {
            sum += original[j];
            count += 1;
          }
          rows[i][key] = count > 0 ? sum / count : 0;
        }
      }
    }

    return rows;
  }, [competitorResults, pendantResults, gamesPerPlayer, revenuePerPlayer, appliedFilter, assumptionsByCompetitor]);

  const weekdayData = useMemo(() => {
    if (!competitorResults.length && !pendantResults.length) return [];

    const byKey = new Map();
    competitorResults.forEach(c => {
      const map = new Map();
      (c?.distributions?.weekday || []).forEach(d => map.set(d.weekday, d.utilizationPercent ?? 0));
      byKey.set(c.competitorKey, map);
    });

    const byPendant = new Map();
    pendantResults.forEach(p => {
      const map = new Map();
      (p?.distributions?.weekday || []).forEach(d => map.set(d.weekday, d.utilizationPercent ?? 0));
      byPendant.set(p.entertainmentId, map);
    });

    return WEEKDAYS.map(d => {
      const row = { weekday: d.key, label: d.label };
      competitorResults.forEach(c => {
        row[c.competitorKey] = byKey.get(c.competitorKey)?.get(d.key) ?? 0;
      });
      pendantResults.forEach(p => {
        row[`funzone_${p.entertainmentId}`] = byPendant.get(p.entertainmentId)?.get(d.key) ?? 0;
      });
      return row;
    });
  }, [competitorResults, pendantResults]);

  const hourlyData = useMemo(() => {
    if (!competitorResults.length && !pendantResults.length) return [];
    const byKey = new Map();
    competitorResults.forEach(c => {
      const map = new Map();
      (c?.distributions?.hourly || []).forEach(d => map.set(d.hour, d.utilizationPercent ?? 0));
      byKey.set(c.competitorKey, map);
    });

    const byPendant = new Map();
    pendantResults.forEach(p => {
      const map = new Map();
      (p?.distributions?.hourly || []).forEach(d => map.set(d.hour, d.utilizationPercent ?? 0));
      byPendant.set(p.entertainmentId, map);
    });

    const rows = Array.from({ length: 24 }).map((_, hour) => {
      const row = { hour, label: `${String(hour).padStart(2, '0')}:00` };
      competitorResults.forEach(c => {
        row[c.competitorKey] = byKey.get(c.competitorKey)?.get(hour) ?? 0;
      });
      pendantResults.forEach(p => {
        row[`funzone_${p.entertainmentId}`] = byPendant.get(p.entertainmentId)?.get(hour) ?? 0;
      });
      return row;
    });

    return rows.filter(r => {
      const competitorNonZero = competitorResults.some(c => (r[c.competitorKey] || 0) > 0);
      const pendantNonZero = pendantResults.some(p => (r[`funzone_${p.entertainmentId}`] || 0) > 0);
      return competitorNonZero || pendantNonZero;
    });
  }, [competitorResults, pendantResults]);

  const hourlyDataByWeekday = useMemo(() => {
    const map = new Map();
    if (!competitorResults.length && !pendantResults.length) return map;

    const competitorByWeekdayByHour = new Map();
    competitorResults.forEach(c => {
      const wMap = new Map();
      (c?.distributions?.hourlyByWeekday || []).forEach(w => {
        const hMap = new Map();
        (w?.hourly || []).forEach(h => hMap.set(h.hour, h.utilizationPercent ?? 0));
        wMap.set(w.weekday, hMap);
      });
      competitorByWeekdayByHour.set(c.competitorKey, wMap);
    });

    const pendantByWeekdayByHour = new Map();
    pendantResults.forEach(p => {
      const wMap = new Map();
      (p?.distributions?.hourlyByWeekday || []).forEach(w => {
        const hMap = new Map();
        (w?.hourly || []).forEach(h => hMap.set(h.hour, h.utilizationPercent ?? 0));
        wMap.set(w.weekday, hMap);
      });
      pendantByWeekdayByHour.set(p.entertainmentId, wMap);
    });

    WEEKDAYS.forEach(d => {
      const rows = Array.from({ length: 24 }).map((_, hour) => {
        const row = { hour, label: `${String(hour).padStart(2, '0')}:00` };
        competitorResults.forEach(c => {
          row[c.competitorKey] =
            competitorByWeekdayByHour.get(c.competitorKey)?.get(d.key)?.get(hour) ?? 0;
        });
        pendantResults.forEach(p => {
          row[`funzone_${p.entertainmentId}`] =
            pendantByWeekdayByHour.get(p.entertainmentId)?.get(d.key)?.get(hour) ?? 0;
        });
        return row;
      });

      const filtered = rows.filter(r => {
        const competitorNonZero = competitorResults.some(c => (r[c.competitorKey] || 0) > 0);
        const pendantNonZero = pendantResults.some(p => (r[`funzone_${p.entertainmentId}`] || 0) > 0);
        return competitorNonZero || pendantNonZero;
      });

      map.set(d.key, filtered);
    });

    return map;
  }, [competitorResults, pendantResults]);

  const revenueWeekdayData = useMemo(() => {
    if (!competitorResults.length && !pendantResults.length) return [];
    const multiCompetitorMode = (appliedFilter?.competitors || []).length > 1;

    const competitorByWeekday = new Map();
    competitorResults.forEach(c => {
      const map = new Map();
      (c?.distributions?.weekday || []).forEach(d => map.set(d.weekday, d));
      competitorByWeekday.set(c.competitorKey, map);
    });

    const pendantByWeekday = new Map();
    pendantResults.forEach(p => {
      const map = new Map();
      (p?.distributions?.weekday || []).forEach(d => map.set(d.weekday, d));
      pendantByWeekday.set(p.entertainmentId, map);
    });

    return WEEKDAYS.map(d => {
      const row = { weekday: d.key, label: d.label };
      competitorResults.forEach(c => {
        const perC = assumptionsByCompetitor?.[c.competitorKey];
        const gp = multiCompetitorMode ? Number(perC?.gamesPerPlayer ?? gamesPerPlayer) : gamesPerPlayer;
        const rp = multiCompetitorMode ? Number(perC?.revenuePerPlayer ?? revenuePerPlayer) : revenuePerPlayer;
        const dist = competitorByWeekday.get(c.competitorKey)?.get(d.key);
        const bookedSlots = Number(dist?.bookedSlots || 0);
        const guests = gp > 0 ? bookedSlots / gp : 0;
        row[c.competitorKey] = guests * rp;
      });
      pendantResults.forEach(p => {
        const dist = pendantByWeekday.get(p.entertainmentId)?.get(d.key);
        row[`funzone_${p.entertainmentId}`] = Number(dist?.revenue || 0);
      });
      return row;
    });
  }, [competitorResults, pendantResults, appliedFilter, assumptionsByCompetitor, gamesPerPlayer, revenuePerPlayer]);

  const revenueHourlyData = useMemo(() => {
    if (!competitorResults.length && !pendantResults.length) return [];
    const multiCompetitorMode = (appliedFilter?.competitors || []).length > 1;

    const byKey = new Map();
    competitorResults.forEach(c => {
      const map = new Map();
      (c?.distributions?.hourly || []).forEach(d => map.set(d.hour, d));
      byKey.set(c.competitorKey, map);
    });

    const byPendant = new Map();
    pendantResults.forEach(p => {
      const map = new Map();
      (p?.distributions?.hourly || []).forEach(d => map.set(d.hour, d));
      byPendant.set(p.entertainmentId, map);
    });

    const rows = Array.from({ length: 24 }).map((_, hour) => {
      const row = { hour, label: `${String(hour).padStart(2, '0')}:00` };
      competitorResults.forEach(c => {
        const perC = assumptionsByCompetitor?.[c.competitorKey];
        const gp = multiCompetitorMode ? Number(perC?.gamesPerPlayer ?? gamesPerPlayer) : gamesPerPlayer;
        const rp = multiCompetitorMode ? Number(perC?.revenuePerPlayer ?? revenuePerPlayer) : revenuePerPlayer;
        const dist = byKey.get(c.competitorKey)?.get(hour);
        const bookedSlots = Number(dist?.bookedSlots || 0);
        const guests = gp > 0 ? bookedSlots / gp : 0;
        row[c.competitorKey] = guests * rp;
      });
      pendantResults.forEach(p => {
        const dist = byPendant.get(p.entertainmentId)?.get(hour);
        row[`funzone_${p.entertainmentId}`] = Number(dist?.revenue || 0);
      });
      return row;
    });

    return rows.filter(r => {
      const competitorNonZero = competitorResults.some(c => (Number(r[c.competitorKey]) || 0) > 0);
      const pendantNonZero = pendantResults.some(p => (Number(r[`funzone_${p.entertainmentId}`]) || 0) > 0);
      return competitorNonZero || pendantNonZero;
    });
  }, [competitorResults, pendantResults, appliedFilter, assumptionsByCompetitor, gamesPerPlayer, revenuePerPlayer]);

  const revenueHourlyDataByWeekday = useMemo(() => {
    const map = new Map();
    if (!competitorResults.length && !pendantResults.length) return map;
    const multiCompetitorMode = (appliedFilter?.competitors || []).length > 1;

    const competitorByWeekdayByHour = new Map();
    competitorResults.forEach(c => {
      const wMap = new Map();
      (c?.distributions?.hourlyByWeekday || []).forEach(w => {
        const hMap = new Map();
        (w?.hourly || []).forEach(h => hMap.set(h.hour, h));
        wMap.set(w.weekday, hMap);
      });
      competitorByWeekdayByHour.set(c.competitorKey, wMap);
    });

    const pendantByWeekdayByHour = new Map();
    pendantResults.forEach(p => {
      const wMap = new Map();
      (p?.distributions?.hourlyByWeekday || []).forEach(w => {
        const hMap = new Map();
        (w?.hourly || []).forEach(h => hMap.set(h.hour, h));
        wMap.set(w.weekday, hMap);
      });
      pendantByWeekdayByHour.set(p.entertainmentId, wMap);
    });

    WEEKDAYS.forEach(d => {
      const rows = Array.from({ length: 24 }).map((_, hour) => {
        const row = { hour, label: `${String(hour).padStart(2, '0')}:00` };
        competitorResults.forEach(c => {
          const perC = assumptionsByCompetitor?.[c.competitorKey];
          const gp = multiCompetitorMode ? Number(perC?.gamesPerPlayer ?? gamesPerPlayer) : gamesPerPlayer;
          const rp = multiCompetitorMode ? Number(perC?.revenuePerPlayer ?? revenuePerPlayer) : revenuePerPlayer;
          const dist = competitorByWeekdayByHour.get(c.competitorKey)?.get(d.key)?.get(hour);
          const bookedSlots = Number(dist?.bookedSlots || 0);
          const guests = gp > 0 ? bookedSlots / gp : 0;
          row[c.competitorKey] = guests * rp;
        });
        pendantResults.forEach(p => {
          const dist = pendantByWeekdayByHour.get(p.entertainmentId)?.get(d.key)?.get(hour);
          row[`funzone_${p.entertainmentId}`] = Number(dist?.revenue || 0);
        });
        return row;
      });

      const filtered = rows.filter(r => {
        const competitorNonZero = competitorResults.some(c => (Number(r[c.competitorKey]) || 0) > 0);
        const pendantNonZero = pendantResults.some(p => (Number(r[`funzone_${p.entertainmentId}`]) || 0) > 0);
        return competitorNonZero || pendantNonZero;
      });

      map.set(d.key, filtered);
    });

    return map;
  }, [competitorResults, pendantResults, appliedFilter, assumptionsByCompetitor, gamesPerPlayer, revenuePerPlayer]);

  const guestsWeekdayData = useMemo(() => {
    if (!competitorResults.length && !pendantResults.length) return [];
    const multiCompetitorMode = (appliedFilter?.competitors || []).length > 1;

    const competitorByWeekday = new Map();
    competitorResults.forEach(c => {
      const map = new Map();
      (c?.distributions?.weekday || []).forEach(d => map.set(d.weekday, d));
      competitorByWeekday.set(c.competitorKey, map);
    });

    const pendantByWeekday = new Map();
    pendantResults.forEach(p => {
      const map = new Map();
      (p?.distributions?.weekday || []).forEach(d => map.set(d.weekday, d));
      pendantByWeekday.set(p.entertainmentId, map);
    });

    return WEEKDAYS.map(d => {
      const row = { weekday: d.key, label: d.label };
      competitorResults.forEach(c => {
        const perC = assumptionsByCompetitor?.[c.competitorKey];
        const gp = multiCompetitorMode ? Number(perC?.gamesPerPlayer ?? gamesPerPlayer) : gamesPerPlayer;
        const dist = competitorByWeekday.get(c.competitorKey)?.get(d.key);
        const bookedSlots = Number(dist?.bookedSlots || 0);
        row[c.competitorKey] = gp > 0 ? bookedSlots / gp : 0;
      });
      pendantResults.forEach(p => {
        const dist = pendantByWeekday.get(p.entertainmentId)?.get(d.key);
        row[`funzone_${p.entertainmentId}`] = Number(dist?.participantCount || 0);
      });
      return row;
    });
  }, [competitorResults, pendantResults, appliedFilter, assumptionsByCompetitor, gamesPerPlayer]);

  const guestsHourlyData = useMemo(() => {
    if (!competitorResults.length && !pendantResults.length) return [];
    const multiCompetitorMode = (appliedFilter?.competitors || []).length > 1;

    const byKey = new Map();
    competitorResults.forEach(c => {
      const map = new Map();
      (c?.distributions?.hourly || []).forEach(d => map.set(d.hour, d));
      byKey.set(c.competitorKey, map);
    });

    const byPendant = new Map();
    pendantResults.forEach(p => {
      const map = new Map();
      (p?.distributions?.hourly || []).forEach(d => map.set(d.hour, d));
      byPendant.set(p.entertainmentId, map);
    });

    const rows = Array.from({ length: 24 }).map((_, hour) => {
      const row = { hour, label: `${String(hour).padStart(2, '0')}:00` };
      competitorResults.forEach(c => {
        const perC = assumptionsByCompetitor?.[c.competitorKey];
        const gp = multiCompetitorMode ? Number(perC?.gamesPerPlayer ?? gamesPerPlayer) : gamesPerPlayer;
        const dist = byKey.get(c.competitorKey)?.get(hour);
        const bookedSlots = Number(dist?.bookedSlots || 0);
        row[c.competitorKey] = gp > 0 ? bookedSlots / gp : 0;
      });
      pendantResults.forEach(p => {
        const dist = byPendant.get(p.entertainmentId)?.get(hour);
        row[`funzone_${p.entertainmentId}`] = Number(dist?.participantCount || 0);
      });
      return row;
    });

    return rows.filter(r => {
      const competitorNonZero = competitorResults.some(c => (Number(r[c.competitorKey]) || 0) > 0);
      const pendantNonZero = pendantResults.some(p => (Number(r[`funzone_${p.entertainmentId}`]) || 0) > 0);
      return competitorNonZero || pendantNonZero;
    });
  }, [competitorResults, pendantResults, appliedFilter, assumptionsByCompetitor, gamesPerPlayer]);

  const guestsHourlyDataByWeekday = useMemo(() => {
    const map = new Map();
    if (!competitorResults.length && !pendantResults.length) return map;
    const multiCompetitorMode = (appliedFilter?.competitors || []).length > 1;

    const competitorByWeekdayByHour = new Map();
    competitorResults.forEach(c => {
      const wMap = new Map();
      (c?.distributions?.hourlyByWeekday || []).forEach(w => {
        const hMap = new Map();
        (w?.hourly || []).forEach(h => hMap.set(h.hour, h));
        wMap.set(w.weekday, hMap);
      });
      competitorByWeekdayByHour.set(c.competitorKey, wMap);
    });

    const pendantByWeekdayByHour = new Map();
    pendantResults.forEach(p => {
      const wMap = new Map();
      (p?.distributions?.hourlyByWeekday || []).forEach(w => {
        const hMap = new Map();
        (w?.hourly || []).forEach(h => hMap.set(h.hour, h));
        wMap.set(w.weekday, hMap);
      });
      pendantByWeekdayByHour.set(p.entertainmentId, wMap);
    });

    WEEKDAYS.forEach(d => {
      const rows = Array.from({ length: 24 }).map((_, hour) => {
        const row = { hour, label: `${String(hour).padStart(2, '0')}:00` };
        competitorResults.forEach(c => {
          const perC = assumptionsByCompetitor?.[c.competitorKey];
          const gp = multiCompetitorMode ? Number(perC?.gamesPerPlayer ?? gamesPerPlayer) : gamesPerPlayer;
          const dist = competitorByWeekdayByHour.get(c.competitorKey)?.get(d.key)?.get(hour);
          const bookedSlots = Number(dist?.bookedSlots || 0);
          row[c.competitorKey] = gp > 0 ? bookedSlots / gp : 0;
        });
        pendantResults.forEach(p => {
          const dist = pendantByWeekdayByHour.get(p.entertainmentId)?.get(d.key)?.get(hour);
          row[`funzone_${p.entertainmentId}`] = Number(dist?.participantCount || 0);
        });
        return row;
      });

      const filtered = rows.filter(r => {
        const competitorNonZero = competitorResults.some(c => (Number(r[c.competitorKey]) || 0) > 0);
        const pendantNonZero = pendantResults.some(p => (Number(r[`funzone_${p.entertainmentId}`]) || 0) > 0);
        return competitorNonZero || pendantNonZero;
      });

      map.set(d.key, filtered);
    });

    return map;
  }, [competitorResults, pendantResults, appliedFilter, assumptionsByCompetitor, gamesPerPlayer]);

  const buildTimelineDataFromHourlyDistribution = useCallback((label, hourly) => {
    const items = Array.isArray(hourly) ? hourly : [];
    const toTimeLabel = v => {
      const s = String(v || '').trim();
      if (!s) return '';
      if (/^\d{2}:\d{2}$/.test(s)) return s;
      return '';
    };

    const parseTimeLabel = time => {
      const t = toTimeLabel(time);
      if (!t) return null;
      const hour = Number(t.slice(0, 2));
      const minutes = Number(t.slice(3, 5));
      if (Number.isNaN(hour) || Number.isNaN(minutes)) return null;
      return { hour, minutes, label: t };
    };

    const timeMap = new Map();
    let maxHour = 10;
    for (const h of items) {
      const timeLabel = toTimeLabel(h?.label || h?.time) || `${String(Number(h?.hour || 0)).padStart(2, '0')}:00`;
      const parsed = parseTimeLabel(timeLabel);
      if (!parsed) continue;
      const booked = Number(h?.bookedSlots || 0);
      const total = Number(h?.totalSlots || 0);
      const pct = Number(h?.utilizationPercent || 0);
      const absoluteCount = Number(h?.absoluteCount ?? h?.participantCount ?? (Number.isFinite(booked) ? booked : 0)) || 0;
      timeMap.set(parsed.label, {
        time: parsed.label,
        occupied: booked,
        free: Math.max(0, total - booked),
        percentageOccupied: Math.max(0, Math.min(100, pct)),
        absoluteCount,
        isSynthetic: false,
      });
      if (parsed.hour > maxHour) maxHour = parsed.hour;
    }

    const startHour = 10;
    const synthesized = [];
    for (let hour = startHour; hour <= maxHour; hour += 1) {
      const full = `${String(hour).padStart(2, '0')}:00`;
      const half = `${String(hour).padStart(2, '0')}:30`;

      const fullFromMap = timeMap.get(full);
      const fullValue =
        fullFromMap || { time: full, occupied: 0, free: 0, percentageOccupied: 0, absoluteCount: 0, isSynthetic: true };
      synthesized.push(fullValue);

      const halfCandidate = timeMap.get(half);
      const halfLooksMissing =
        halfCandidate &&
        (Number(halfCandidate.occupied) || 0) === 0 &&
        (Number(halfCandidate.free) || 0) === 0 &&
        (Number(halfCandidate.percentageOccupied) || 0) === 0 &&
        ((Number(fullValue.occupied) || 0) > 0 || (Number(fullValue.free) || 0) > 0);
      const halfValue =
        !halfCandidate || halfLooksMissing
          ? { ...fullValue, time: half, isSynthetic: true }
          : { ...halfCandidate, isSynthetic: false };
      synthesized.push(halfValue);
    }

    const filteredTimes = synthesized.filter(t => {
      const parsed = parseTimeLabel(t?.time);
      return parsed && parsed.hour >= 10;
    });

    const usesResources = filteredTimes.some(t => (t.occupied || 0) > 0 || (t.free || 0) > 0);

    return {
      entertainmentName: label,
      usesResources,
      times: filteredTimes,
    };
  }, []);

  const buildWeekdayLabel = useCallback(weekdayKey => {
    return WEEKDAYS.find(d => d.key === weekdayKey)?.label || String(weekdayKey);
  }, []);

  const buildCompactCsvText = useCallback(() => {
    const start = appliedFilter?.startDate || '';
    const end = appliedFilter?.endDate || '';
    const selectedKeys = Array.isArray(appliedFilter?.competitors) ? appliedFilter.competitors : [];
    const multiCompetitorMode = selectedKeys.length > 1;

    const competitors = (Array.isArray(stats?.competitors) ? stats.competitors : []).filter(c =>
      selectedKeys.includes(c?.competitorKey)
    );
    const pendants = Array.isArray(ownStats?.entertainments) ? ownStats.entertainments : [];

    const rows = [];
    rows.push('sep=;');
    rows.push(['meta', 'generatedAt', new Date().toISOString()].map(csvEscape).join(';'));
    rows.push(['meta', 'filterStartDate', start].map(csvEscape).join(';'));
    rows.push(['meta', 'filterEndDate', end].map(csvEscape).join(';'));
    rows.push(['meta', 'competitors', selectedKeys.join(',')].map(csvEscape).join(';'));

    competitors.forEach(c => {
      const perC = assumptionsByCompetitor?.[c.competitorKey];
      const gp = multiCompetitorMode ? Number(perC?.gamesPerPlayer ?? gamesPerPlayer) : gamesPerPlayer;
      const rp = multiCompetitorMode ? Number(perC?.revenuePerPlayer ?? revenuePerPlayer) : revenuePerPlayer;
      rows.push(
        ['meta', 'assumption', c.competitorKey, formatDecimalForCsv(gp), formatDecimalForCsv(rp)]
          .map(csvEscape)
          .join(';')
      );
    });

    rows.push('');
    rows.push(
      [
        'entityType',
        'entityKey',
        'entityName',
        'city',
        'series',
        'date',
        'weekday',
        'hour',
        'time',
        'bookedSlots',
        'totalSlots',
        'utilizationPercent',
        'guests',
        'revenue',
        'revenueIsEstimated',
      ]
        .map(csvEscape)
        .join(';')
    );

    const pushRow = r => rows.push(r.map(csvEscape).join(';'));

    const getAssumption = competitorKey => {
      const perC = assumptionsByCompetitor?.[competitorKey];
      const gp = multiCompetitorMode ? Number(perC?.gamesPerPlayer ?? gamesPerPlayer) : gamesPerPlayer;
      const rp = multiCompetitorMode ? Number(perC?.revenuePerPlayer ?? revenuePerPlayer) : revenuePerPlayer;
      return { gp, rp };
    };

    const estimateGuests = (bookedSlots, competitorKey) => {
      const { gp } = getAssumption(competitorKey);
      return gp > 0 ? Number(bookedSlots || 0) / gp : 0;
    };

    const estimateRevenue = (bookedSlots, competitorKey) => {
      const { rp } = getAssumption(competitorKey);
      return estimateGuests(bookedSlots, competitorKey) * rp;
    };

    for (const c of competitors) {
      const entityType = 'competitor';
      const entityKey = c?.competitorKey || '';
      const entityName = c?.competitorName || entityKey;
      const city = c?.city || '';

      (c?.timeSeries?.daily || []).forEach(d => {
        const date = String(d?.date || '').trim();
        if (!date) return;
        const weekday = String(dayjs(date).day());
        const bookedSlots = Number(d?.bookedSlots || 0);
        const totalSlots = Number(d?.totalSlots || 0);
        const util = Number(d?.utilizationPercent || 0);
        if (!(bookedSlots > 0 || totalSlots > 0 || util > 0)) return;
        pushRow([
          entityType,
          entityKey,
          entityName,
          city,
          'daily',
          date,
          weekday,
          '',
          '',
          bookedSlots,
          totalSlots,
          formatDecimalForCsv(util),
          formatDecimalForCsv(estimateGuests(bookedSlots, entityKey)),
          formatDecimalForCsv(roundToTwoSafe(estimateRevenue(bookedSlots, entityKey))),
          'true',
        ]);
      });

      const weekdayDist = Array.isArray(c?.distributions?.weekday) ? c.distributions.weekday : [];
      weekdayDist.forEach(w => {
        const weekday = w?.weekday;
        if (weekday === null || weekday === undefined) return;
        const bookedSlots = Number(w?.bookedSlots || 0);
        const totalSlots = Number(w?.totalSlots || 0);
        const util = Number(w?.utilizationPercent || 0);
        if (!(bookedSlots > 0 || totalSlots > 0 || util > 0)) return;
        pushRow([
          entityType,
          entityKey,
          entityName,
          city,
          'weekday',
          '',
          weekday,
          '',
          buildWeekdayLabel(weekday),
          bookedSlots,
          totalSlots,
          formatDecimalForCsv(util),
          formatDecimalForCsv(estimateGuests(bookedSlots, entityKey)),
          formatDecimalForCsv(roundToTwoSafe(estimateRevenue(bookedSlots, entityKey))),
          'true',
        ]);
      });

      const hourlyDist = Array.isArray(c?.distributions?.hourly) ? c.distributions.hourly : [];
      hourlyDist.forEach(h => {
        const hour = h?.hour;
        if (hour === null || hour === undefined) return;
        const bookedSlots = Number(h?.bookedSlots || 0);
        const totalSlots = Number(h?.totalSlots || 0);
        const util = Number(h?.utilizationPercent || 0);
        if (!(bookedSlots > 0 || totalSlots > 0 || util > 0)) return;
        pushRow([
          entityType,
          entityKey,
          entityName,
          city,
          'hourly',
          '',
          '',
          hour,
          h?.label || '',
          bookedSlots,
          totalSlots,
          formatDecimalForCsv(util),
          formatDecimalForCsv(estimateGuests(bookedSlots, entityKey)),
          formatDecimalForCsv(roundToTwoSafe(estimateRevenue(bookedSlots, entityKey))),
          'true',
        ]);
      });

      const hourlyByWeekday = Array.isArray(c?.distributions?.hourlyByWeekday) ? c.distributions.hourlyByWeekday : [];
      hourlyByWeekday.forEach(w => {
        const weekday = w?.weekday;
        (w?.hourly || []).forEach(h => {
          const hour = h?.hour;
          if (weekday === null || weekday === undefined || hour === null || hour === undefined) return;
          const bookedSlots = Number(h?.bookedSlots || 0);
          const totalSlots = Number(h?.totalSlots || 0);
          const util = Number(h?.utilizationPercent || 0);
          if (!(bookedSlots > 0 || totalSlots > 0 || util > 0)) return;
          pushRow([
            entityType,
            entityKey,
            entityName,
            city,
            'hourlyByWeekday',
            '',
            weekday,
            hour,
            h?.label || '',
            bookedSlots,
            totalSlots,
            formatDecimalForCsv(util),
            formatDecimalForCsv(estimateGuests(bookedSlots, entityKey)),
            formatDecimalForCsv(roundToTwoSafe(estimateRevenue(bookedSlots, entityKey))),
            'true',
          ]);
        });
      });

      const halfHourly = Array.isArray(c?.distributions?.halfHourly) ? c.distributions.halfHourly : [];
      halfHourly.forEach(h => {
        const time = String(h?.label || '').trim();
        if (!time) return;
        const bookedSlots = Number(h?.bookedSlots || 0);
        const totalSlots = Number(h?.totalSlots || 0);
        const util = Number(h?.utilizationPercent || 0);
        if (!(bookedSlots > 0 || totalSlots > 0 || util > 0)) return;
        pushRow([
          entityType,
          entityKey,
          entityName,
          city,
          'halfHourly',
          '',
          '',
          h?.hour ?? '',
          time,
          bookedSlots,
          totalSlots,
          formatDecimalForCsv(util),
          formatDecimalForCsv(estimateGuests(bookedSlots, entityKey)),
          formatDecimalForCsv(roundToTwoSafe(estimateRevenue(bookedSlots, entityKey))),
          'true',
        ]);
      });

      const halfHourlyByWeekday = Array.isArray(c?.distributions?.halfHourlyByWeekday) ? c.distributions.halfHourlyByWeekday : [];
      halfHourlyByWeekday.forEach(w => {
        const weekday = w?.weekday;
        (w?.halfHourly || []).forEach(h => {
          const time = String(h?.label || '').trim();
          if (weekday === null || weekday === undefined || !time) return;
          const bookedSlots = Number(h?.bookedSlots || 0);
          const totalSlots = Number(h?.totalSlots || 0);
          const util = Number(h?.utilizationPercent || 0);
          if (!(bookedSlots > 0 || totalSlots > 0 || util > 0)) return;
          pushRow([
            entityType,
            entityKey,
            entityName,
            city,
            'halfHourlyByWeekday',
            '',
            weekday,
            h?.hour ?? '',
            time,
            bookedSlots,
            totalSlots,
            formatDecimalForCsv(util),
            formatDecimalForCsv(estimateGuests(bookedSlots, entityKey)),
            formatDecimalForCsv(roundToTwoSafe(estimateRevenue(bookedSlots, entityKey))),
            'true',
          ]);
        });
      });
    }

    for (const p of pendants) {
      const entityType = 'funzone';
      const entityKey = `funzone_${p?.entertainmentId || ''}`;
      const entityName = p?.entertainmentName || entityKey;
      const city = '';

      (p?.timeSeries?.daily || []).forEach(d => {
        const date = String(d?.date || '').trim();
        if (!date) return;
        const weekday = String(dayjs(date).day());
        const bookedSlots = Number(d?.bookedSlots || 0);
        const totalSlots = Number(d?.totalSlots || 0);
        const util = Number(d?.utilizationPercent || 0);
        const revenue = Number(d?.revenue || 0);
        const guests = Number(d?.participantCount || 0);
        if (!(bookedSlots > 0 || totalSlots > 0 || util > 0 || revenue > 0 || guests > 0)) return;
        pushRow([
          entityType,
          entityKey,
          entityName,
          city,
          'daily',
          date,
          weekday,
          '',
          '',
          bookedSlots,
          totalSlots,
          formatDecimalForCsv(util),
          formatDecimalForCsv(guests),
          formatDecimalForCsv(roundToTwoSafe(revenue)),
          'false',
        ]);
      });

      (p?.distributions?.weekday || []).forEach(w => {
        const weekday = w?.weekday;
        if (weekday === null || weekday === undefined) return;
        const bookedSlots = Number(w?.bookedSlots || 0);
        const totalSlots = Number(w?.totalSlots || 0);
        const util = Number(w?.utilizationPercent || 0);
        const revenue = Number(w?.revenue || 0);
        const guests = Number(w?.participantCount || 0);
        if (!(bookedSlots > 0 || totalSlots > 0 || util > 0 || revenue > 0 || guests > 0)) return;
        pushRow([
          entityType,
          entityKey,
          entityName,
          city,
          'weekday',
          '',
          weekday,
          '',
          buildWeekdayLabel(weekday),
          bookedSlots,
          totalSlots,
          formatDecimalForCsv(util),
          formatDecimalForCsv(guests),
          formatDecimalForCsv(roundToTwoSafe(revenue)),
          'false',
        ]);
      });

      (p?.distributions?.hourly || []).forEach(h => {
        const hour = h?.hour;
        if (hour === null || hour === undefined) return;
        const bookedSlots = Number(h?.bookedSlots || 0);
        const totalSlots = Number(h?.totalSlots || 0);
        const util = Number(h?.utilizationPercent || 0);
        const revenue = Number(h?.revenue || 0);
        const guests = Number(h?.participantCount || 0);
        if (!(bookedSlots > 0 || totalSlots > 0 || util > 0 || revenue > 0 || guests > 0)) return;
        pushRow([
          entityType,
          entityKey,
          entityName,
          city,
          'hourly',
          '',
          '',
          hour,
          h?.label || '',
          bookedSlots,
          totalSlots,
          formatDecimalForCsv(util),
          formatDecimalForCsv(guests),
          formatDecimalForCsv(roundToTwoSafe(revenue)),
          'false',
        ]);
      });

      (p?.distributions?.hourlyByWeekday || []).forEach(w => {
        const weekday = w?.weekday;
        (w?.hourly || []).forEach(h => {
          const hour = h?.hour;
          if (weekday === null || weekday === undefined || hour === null || hour === undefined) return;
          const bookedSlots = Number(h?.bookedSlots || 0);
          const totalSlots = Number(h?.totalSlots || 0);
          const util = Number(h?.utilizationPercent || 0);
          const revenue = Number(h?.revenue || 0);
          const guests = Number(h?.participantCount || 0);
          if (!(bookedSlots > 0 || totalSlots > 0 || util > 0 || revenue > 0 || guests > 0)) return;
          pushRow([
            entityType,
            entityKey,
            entityName,
            city,
            'hourlyByWeekday',
            '',
            weekday,
            hour,
            h?.label || '',
            bookedSlots,
            totalSlots,
            formatDecimalForCsv(util),
            formatDecimalForCsv(guests),
            formatDecimalForCsv(roundToTwoSafe(revenue)),
            'false',
          ]);
        });
      });

      (p?.distributions?.halfHourly || []).forEach(h => {
        const time = String(h?.label || '').trim();
        if (!time) return;
        const bookedSlots = Number(h?.bookedSlots || 0);
        const totalSlots = Number(h?.totalSlots || 0);
        const util = Number(h?.utilizationPercent || 0);
        const revenue = Number(h?.revenue || 0);
        const guests = Number(h?.participantCount || 0);
        if (!(bookedSlots > 0 || totalSlots > 0 || util > 0 || revenue > 0 || guests > 0)) return;
        pushRow([
          entityType,
          entityKey,
          entityName,
          city,
          'halfHourly',
          '',
          '',
          h?.hour ?? '',
          time,
          bookedSlots,
          totalSlots,
          formatDecimalForCsv(util),
          formatDecimalForCsv(guests),
          formatDecimalForCsv(roundToTwoSafe(revenue)),
          'false',
        ]);
      });

      (p?.distributions?.halfHourlyByWeekday || []).forEach(w => {
        const weekday = w?.weekday;
        (w?.halfHourly || []).forEach(h => {
          const time = String(h?.label || '').trim();
          if (weekday === null || weekday === undefined || !time) return;
          const bookedSlots = Number(h?.bookedSlots || 0);
          const totalSlots = Number(h?.totalSlots || 0);
          const util = Number(h?.utilizationPercent || 0);
          const revenue = Number(h?.revenue || 0);
          const guests = Number(h?.participantCount || 0);
          if (!(bookedSlots > 0 || totalSlots > 0 || util > 0 || revenue > 0 || guests > 0)) return;
          pushRow([
            entityType,
            entityKey,
            entityName,
            city,
            'halfHourlyByWeekday',
            '',
            weekday,
            h?.hour ?? '',
            time,
            bookedSlots,
            totalSlots,
            formatDecimalForCsv(util),
            formatDecimalForCsv(guests),
            formatDecimalForCsv(roundToTwoSafe(revenue)),
            'false',
          ]);
        });
      });
    }

    return rows.join('\r\n');
  }, [appliedFilter, stats, ownStats, assumptionsByCompetitor, gamesPerPlayer, revenuePerPlayer, buildWeekdayLabel]);

  const buildCsvText = useCallback(() => {
    const start = appliedFilter?.startDate || '';
    const end = appliedFilter?.endDate || '';
    const competitorKeys = Array.isArray(appliedFilter?.competitors) ? appliedFilter.competitors : [];
    const multiCompetitorMode = competitorKeys.length > 1;

    const rows = [];
    rows.push(['sep=;'].join(''));
    rows.push(
      [
        'entityType',
        'entityKey',
        'entityName',
        'city',
        'apiLocation',
        'seriesType',
        'date',
        'weekday',
        'hour',
        'label',
        'bookedSlots',
        'totalSlots',
        'utilizationPercent',
        'revenue',
        'participantCount',
        'revenueIsEstimated',
        'gamesPerPlayer',
        'revenuePerPlayer',
        'filterStartDate',
        'filterEndDate',
      ]
        .map(csvEscape)
        .join(';')
    );

    const competitors = Array.isArray(stats?.competitors) ? stats.competitors : [];
    const pendants = Array.isArray(ownStats?.entertainments) ? ownStats.entertainments : [];

    const shouldIncludeDistributionRow = ({ bookedSlots, totalSlots, utilizationPercent }) => {
      const booked = Number(bookedSlots || 0);
      const total = Number(totalSlots || 0);
      const util = Number(utilizationPercent || 0);
      return total > 0 || booked > 0 || util > 0;
    };

    const shouldIncludeDailyRow = ({ bookedSlots, totalSlots, utilizationPercent, revenue, participantCount }) => {
      const booked = Number(bookedSlots || 0);
      const total = Number(totalSlots || 0);
      const util = Number(utilizationPercent || 0);
      const rev = Number(revenue || 0);
      const part = Number(participantCount || 0);
      return total > 0 || booked > 0 || util > 0 || rev > 0 || part > 0;
    };

    for (const c of competitors) {
      if (!competitorKeys.includes(c?.competitorKey)) continue;
      const entityType = 'competitor';
      const entityKey = c?.competitorKey || '';
      const entityName = c?.competitorName || entityKey;
      const city = c?.city || '';
      const apiLocation = c?.apiLocation || '';
      const perC = assumptionsByCompetitor?.[entityKey];
      const gp = multiCompetitorMode ? Number(perC?.gamesPerPlayer ?? gamesPerPlayer) : gamesPerPlayer;
      const rp = multiCompetitorMode ? Number(perC?.revenuePerPlayer ?? revenuePerPlayer) : revenuePerPlayer;

      (c?.timeSeries?.daily || []).forEach(d => {
        const dayKey = String(d?.date || '').trim();
        const weekday = dayKey ? dayjs(dayKey).day() : '';
        const bookedSlots = Number(d?.bookedSlots || 0);
        const totalSlots = Number(d?.totalSlots || 0);
        const utilizationPercent = Number(d?.utilizationPercent || 0);
        const guests = gp > 0 ? bookedSlots / gp : 0;
        const revenue = guests * rp;
        if (
          !shouldIncludeDailyRow({
            bookedSlots,
            totalSlots,
            utilizationPercent,
            revenue,
            participantCount: 0,
          })
        ) {
          return;
        }
        rows.push(
          [
            entityType,
            entityKey,
            entityName,
            city,
            apiLocation,
            'daily',
            dayKey,
            weekday,
            '',
            dayKey,
            bookedSlots,
            totalSlots,
            formatDecimalForCsv(utilizationPercent),
            formatDecimalForCsv(roundToTwoSafe(revenue)),
            '',
            'true',
            formatDecimalForCsv(gp),
            formatDecimalForCsv(rp),
            start,
            end,
          ]
            .map(csvEscape)
            .join(';')
        );
      });

      (c?.distributions?.weekday || []).forEach(w => {
        if (
          !shouldIncludeDistributionRow({
            bookedSlots: w?.bookedSlots,
            totalSlots: w?.totalSlots,
            utilizationPercent: w?.utilizationPercent,
          })
        ) {
          return;
        }
        rows.push(
          [
            entityType,
            entityKey,
            entityName,
            city,
            apiLocation,
            'weekdayAll',
            '',
            w?.weekday ?? '',
            '',
            buildWeekdayLabel(w?.weekday),
            w?.bookedSlots ?? '',
            w?.totalSlots ?? '',
            formatDecimalForCsv(w?.utilizationPercent ?? ''),
            '',
            '',
            'true',
            formatDecimalForCsv(gp),
            formatDecimalForCsv(rp),
            start,
            end,
          ]
            .map(csvEscape)
            .join(';')
        );
      });

      (c?.distributions?.hourly || []).forEach(h => {
        if ((c?.distributions?.halfHourly && c.distributions.halfHourly.length) || (c?.distributions?.halfHourlyByWeekday && c.distributions.halfHourlyByWeekday.length)) {
          return;
        }
        if (
          !shouldIncludeDistributionRow({
            bookedSlots: h?.bookedSlots,
            totalSlots: h?.totalSlots,
            utilizationPercent: h?.utilizationPercent,
          })
        ) {
          return;
        }
        rows.push(
          [
            entityType,
            entityKey,
            entityName,
            city,
            apiLocation,
            'hourlyAll',
            '',
            '',
            h?.hour ?? '',
            h?.label ?? '',
            h?.bookedSlots ?? '',
            h?.totalSlots ?? '',
            formatDecimalForCsv(h?.utilizationPercent ?? ''),
            '',
            '',
            'true',
            formatDecimalForCsv(gp),
            formatDecimalForCsv(rp),
            start,
            end,
          ]
            .map(csvEscape)
            .join(';')
        );
      });

      (c?.distributions?.hourlyByWeekday || []).forEach(w => {
        if ((c?.distributions?.halfHourly && c.distributions.halfHourly.length) || (c?.distributions?.halfHourlyByWeekday && c.distributions.halfHourlyByWeekday.length)) {
          return;
        }
        (w?.hourly || []).forEach(h => {
          if (
            !shouldIncludeDistributionRow({
              bookedSlots: h?.bookedSlots,
              totalSlots: h?.totalSlots,
              utilizationPercent: h?.utilizationPercent,
            })
          ) {
            return;
          }
          rows.push(
            [
              entityType,
              entityKey,
              entityName,
              city,
              apiLocation,
              'hourlyByWeekday',
              '',
              w?.weekday ?? '',
              h?.hour ?? '',
              `${buildWeekdayLabel(w?.weekday)} ${h?.label ?? ''}`.trim(),
              h?.bookedSlots ?? '',
              h?.totalSlots ?? '',
              formatDecimalForCsv(h?.utilizationPercent ?? ''),
              '',
              '',
              'true',
              formatDecimalForCsv(gp),
              formatDecimalForCsv(rp),
              start,
              end,
            ]
              .map(csvEscape)
              .join(';')
          );
        });
      });
    }

    for (const p of pendants) {
      const entityType = 'funzone';
      const entityKey = `funzone_${p?.entertainmentId || ''}`;
      const entityName = p?.entertainmentName || entityKey;

      (p?.timeSeries?.daily || []).forEach(d => {
        const dayKey = String(d?.date || '').trim();
        const weekday = dayKey ? dayjs(dayKey).day() : '';
        const bookedSlots = Number(d?.bookedSlots || 0);
        const totalSlots = Number(d?.totalSlots || 0);
        const utilizationPercent = Number(d?.utilizationPercent || 0);
        const revenue = Number(d?.revenue || 0);
        const participantCount = Number(d?.participantCount || 0);
        if (
          !shouldIncludeDailyRow({
            bookedSlots,
            totalSlots,
            utilizationPercent,
            revenue,
            participantCount,
          })
        ) {
          return;
        }
        rows.push(
          [
            entityType,
            entityKey,
            entityName,
            '',
            '',
            'daily',
            dayKey,
            weekday,
            '',
            dayKey,
            bookedSlots,
            totalSlots,
            formatDecimalForCsv(utilizationPercent),
            formatDecimalForCsv(roundToTwoSafe(revenue)),
            participantCount,
            'false',
            '',
            '',
            start,
            end,
          ]
            .map(csvEscape)
            .join(';')
        );
      });

      (p?.distributions?.weekday || []).forEach(w => {
        if (
          !shouldIncludeDistributionRow({
            bookedSlots: w?.bookedSlots,
            totalSlots: w?.totalSlots,
            utilizationPercent: w?.utilizationPercent,
          })
        ) {
          return;
        }
        rows.push(
          [
            entityType,
            entityKey,
            entityName,
            '',
            '',
            'weekdayAll',
            '',
            w?.weekday ?? '',
            '',
            buildWeekdayLabel(w?.weekday),
            w?.bookedSlots ?? '',
            w?.totalSlots ?? '',
            formatDecimalForCsv(w?.utilizationPercent ?? ''),
            '',
            '',
            'false',
            '',
            '',
            start,
            end,
          ]
            .map(csvEscape)
            .join(';')
        );
      });

      (p?.distributions?.hourly || []).forEach(h => {
        if ((p?.distributions?.halfHourly && p.distributions.halfHourly.length) || (p?.distributions?.halfHourlyByWeekday && p.distributions.halfHourlyByWeekday.length)) {
          return;
        }
        if (
          !shouldIncludeDistributionRow({
            bookedSlots: h?.bookedSlots,
            totalSlots: h?.totalSlots,
            utilizationPercent: h?.utilizationPercent,
          })
        ) {
          return;
        }
        rows.push(
          [
            entityType,
            entityKey,
            entityName,
            '',
            '',
            'hourlyAll',
            '',
            '',
            h?.hour ?? '',
            h?.label ?? '',
            h?.bookedSlots ?? '',
            h?.totalSlots ?? '',
            formatDecimalForCsv(h?.utilizationPercent ?? ''),
            '',
            '',
            'false',
            '',
            '',
            start,
            end,
          ]
            .map(csvEscape)
            .join(';')
        );
      });

      (p?.distributions?.hourlyByWeekday || []).forEach(w => {
        if ((p?.distributions?.halfHourly && p.distributions.halfHourly.length) || (p?.distributions?.halfHourlyByWeekday && p.distributions.halfHourlyByWeekday.length)) {
          return;
        }
        (w?.hourly || []).forEach(h => {
          if (
            !shouldIncludeDistributionRow({
              bookedSlots: h?.bookedSlots,
              totalSlots: h?.totalSlots,
              utilizationPercent: h?.utilizationPercent,
            })
          ) {
            return;
          }
          rows.push(
            [
              entityType,
              entityKey,
              entityName,
              '',
              '',
              'hourlyByWeekday',
              '',
              w?.weekday ?? '',
              h?.hour ?? '',
              `${buildWeekdayLabel(w?.weekday)} ${h?.label ?? ''}`.trim(),
              h?.bookedSlots ?? '',
              h?.totalSlots ?? '',
              formatDecimalForCsv(h?.utilizationPercent ?? ''),
              '',
              '',
              'false',
              '',
              '',
              start,
              end,
            ]
              .map(csvEscape)
              .join(';')
          );
        });
      });
    }

    return rows.join('\r\n');
  }, [appliedFilter, stats, ownStats, buildWeekdayLabel, gamesPerPlayer, revenuePerPlayer, assumptionsByCompetitor]);

  const downloadCsv = useCallback(() => {
    if (!appliedFilter?.competitors?.length) {
      toast.error('Bitte wählen Sie mindestens einen Konkurrenten aus');
      return;
    }
    const csv = buildCompactCsvText();
    const filename = `competitor-availability_${appliedFilter.startDate || 'start'}_${appliedFilter.endDate || 'end'}.csv`;
    downloadTextFile({ filename, text: csv, mimeType: 'text/csv;charset=utf-8' });
  }, [appliedFilter, buildCompactCsvText]);

  const cards = useMemo(() => {
    const multiCompetitorMode = (appliedFilter?.competitors || []).length > 1;
    const competitorCards = competitorResults.map(c => {
      const bookedSlots = c?.totals?.bookedSlots ?? 0;
      const totalSlots = c?.totals?.totalSlots ?? 0;
      const utilization = c?.totals?.utilizationPercent ?? 0;
      const perC = assumptionsByCompetitor?.[c?.competitorKey];
      const gp = multiCompetitorMode ? Number(perC?.gamesPerPlayer ?? gamesPerPlayer) : gamesPerPlayer;
      const rp = multiCompetitorMode ? Number(perC?.revenuePerPlayer ?? revenuePerPlayer) : revenuePerPlayer;
      const guests = gp > 0 ? bookedSlots / gp : 0;
      const revenue = guests * rp;
      return {
        key: c.competitorKey,
        title: `${c.competitorName || c.competitorKey}${c.city ? ` (${c.city})` : ''}`,
        items: [
          { label: 'Auslastung (Ø)', value: `${percentFormatter.format(utilization)} %` },
          { label: 'Gebuchte Slots', value: bookedSlots },
          { label: 'Max. Kapazität', value: totalSlots },
          { label: 'Gäste (geschätzt)', value: Math.round(guests) },
          { label: 'Umsatz (geschätzt)', value: formatCurrency(revenue) },
        ],
      };
    });

    const pendantCards = pendantResults.map(p => {
      const bookedSlots = p?.totals?.bookedSlots ?? 0;
      const totalSlots = p?.totals?.totalSlots ?? 0;
      const utilization = p?.totals?.utilizationPercent ?? 0;
      const guests = p?.totals?.participantCount ?? 0;
      const revenue = p?.totals?.revenue ?? 0;
      return {
        key: `funzone_${p.entertainmentId}`,
        title: `FunZone: ${p?.entertainmentName || 'Entertainment'}`,
        items: [
          { label: 'Auslastung (Ø)', value: `${percentFormatter.format(utilization)} %` },
          { label: 'Gebuchte Slots', value: bookedSlots },
          { label: 'Max. Kapazität', value: totalSlots },
          { label: 'Gäste', value: Math.round(guests) },
          { label: 'Umsatz', value: formatCurrency(revenue) },
        ],
      };
    });

    return [...competitorCards, ...pendantCards];
  }, [competitorResults, pendantResults, gamesPerPlayer, revenuePerPlayer, appliedFilter, assumptionsByCompetitor]);

  const COLORS = ['#26ad21', '#f53905', '#2196f3', '#9c27b0', '#ff9800', '#00bcd4'];
  const competitorColors = useMemo(() => {
    const map = new Map();
    competitorResults.forEach((c, idx) => map.set(c.competitorKey, COLORS[idx % COLORS.length]));
    return map;
  }, [competitorResults]);

  const OWN_COLORS = ['#e0e0e0', '#b0bec5', '#c5e1a5', '#ffe082'];
  const pendantColors = useMemo(() => {
    const map = new Map();
    pendantResults.forEach((p, idx) => map.set(p.entertainmentId, OWN_COLORS[idx % OWN_COLORS.length]));
    return map;
  }, [pendantResults]);

  const chartGroups = useMemo(() => {
    const competitorKeys = Array.isArray(appliedFilter?.competitors) ? appliedFilter.competitors : [];
    if (!competitorKeys.length) return [];

    const pendantIds = Array.from(
      new Set(
        (appliedFilter?.benchmarkEntertainmentIds || [])
          .map(v => String(v || '').trim())
          .filter(Boolean)
      )
    ).filter(id => pendantsById.has(id));

    return [
      {
        key: 'all',
        title: 'Vergleich',
        competitorKeys,
        pendantIds,
      },
    ];
  }, [appliedFilter, pendantsById]);

  const timeSeriesDataByGroup = useMemo(() => {
    const map = new Map();
    (chartGroups || []).forEach(group => {
      const keysToCheck = [];
      (group.competitorKeys || []).forEach(k => {
        keysToCheck.push(`${k}__util`, `${k}__rev`);
      });
      (group.pendantIds || []).forEach(pid => {
        const key = `funzone_${pid}`;
        keysToCheck.push(`${key}__util`, `${key}__rev`);
      });

      // Trim leading rows where all series are 0.
      let startIdx = 0;
      while (startIdx < (timeSeriesData || []).length) {
        const row = timeSeriesData[startIdx] || {};
        const hasAnyValue = keysToCheck.some(k => (Number(row[k]) || 0) > 0);
        if (hasAnyValue) break;
        startIdx += 1;
      }

      const trimmed = startIdx < (timeSeriesData || []).length ? timeSeriesData.slice(startIdx) : [];
      map.set(group.key, trimmed);
    });
    return map;
  }, [chartGroups, timeSeriesData]);

  const timeSeriesChartExtrasByGroup = useMemo(() => {
    const map = new Map();
    const todayStr = dayjs().format('YYYY-MM-DD');
    for (const [key, data] of timeSeriesDataByGroup.entries()) {
      if (!Array.isArray(data) || !data.length) {
        map.set(key, { ticks: undefined, futureX1: null, futureX2: null, todayLabel: null });
        continue;
      }

      const monthStartLabels = [];
      const seenMonths = new Set();
      for (const row of data) {
        if (!row?.date) continue;
        const month = String(row.date).slice(0, 7);
        if (seenMonths.has(month)) continue;
        seenMonths.add(month);
        monthStartLabels.push(row.label);
      }

      let ticks;
      if (monthStartLabels.length >= 3) {
        ticks = monthStartLabels;
      } else if (data.length <= 7) {
        ticks = data.map(r => r.label);
      } else {
        const step = Math.max(1, Math.floor(data.length / 6));
        ticks = [];
        for (let i = 0; i < data.length; i += step) ticks.push(data[i].label);
        const lastLabel = data[data.length - 1].label;
        if (ticks[ticks.length - 1] !== lastLabel) ticks.push(lastLabel);
      }

      const firstFutureIdx = data.findIndex(r => String(r.date) > todayStr);
      const futureX1 = firstFutureIdx > 0 ? data[firstFutureIdx].label : null;
      const futureX2 = firstFutureIdx > 0 ? data[data.length - 1].label : null;
      const todayIdx = data.findIndex(r => String(r.date) === todayStr);
      const todayLabel = todayIdx >= 0 ? data[todayIdx].label : null;

      map.set(key, { ticks, futureX1, futureX2, todayLabel });
    }
    return map;
  }, [timeSeriesDataByGroup]);

  const freshnessSummaryByGroup = useMemo(() => {
    const map = new Map();

    (chartGroups || []).forEach(group => {
      const series = competitorResults
        .filter(c => (group.competitorKeys || []).includes(c.competitorKey))
        .map(c => ({
          competitorKey: c.competitorKey,
          label: c.competitorName || c.competitorKey,
          color: competitorColors.get(c.competitorKey) || '#90caf9',
        }));

      const points = [];

      (timeSeriesDataByGroup.get(group.key) || []).forEach(row => {
        (Array.isArray(row?.__freshnessItems) ? row.__freshnessItems : []).forEach(item => {
          if (!(group.competitorKeys || []).includes(item.entityKey)) return;
          const scrapedAt = dayjs(item.scrapedAt);
          if (!scrapedAt.isValid()) return;

          const ageHours = Math.max(0, dayjs().diff(scrapedAt, 'minute') / 60);
          const isHistoricalTarget = isHistoricalTargetDate(item.targetDate || row.date);
          points.push({
            competitorKey: item.entityKey,
            competitorLabel: item.label,
            date: row.date,
            targetDate: item.targetDate || row.date,
            scrapedAt: scrapedAt.toISOString(),
            ageHours,
            isHistoricalTarget,
          });
        });
      });

      const activePoints = points.filter(point => !point.isHistoricalTarget);
      const historicalPoints = points.filter(point => point.isHistoricalTarget);
      const sortedByFreshness = [...activePoints].sort((a, b) => a.ageHours - b.ageHours);

      const buildDistribution = items => {
        const total = items.length;
        const activeItems = items.filter(item => !item.isHistoricalTarget);
        const distributions = [];

        const historicalCount = items.filter(item => item.isHistoricalTarget).length;
        if (historicalCount > 0) {
          distributions.push({
            key: HISTORICAL_TARGET_BUCKET.key,
            label: HISTORICAL_TARGET_BUCKET.label,
            count: historicalCount,
            percent: total > 0 ? (historicalCount / total) * 100 : 0,
          });
        }

        return [
          ...distributions,
          ...AGE_BUCKETS.map(bucket => {
            const count = activeItems.filter(item => getAgeBucketKey(item.ageHours) === bucket.key).length;
            const percent = total > 0 ? (count / total) * 100 : 0;
            return {
              key: bucket.key,
              label: bucket.label,
              count,
              percent,
            };
          }),
        ];
      };

      map.set(group.key, {
        series,
        points,
        activePoints,
        historicalPoints,
        overallDistribution: buildDistribution(points),
        perCompetitorDistribution: series.map(entry => ({
          ...entry,
          total: points.filter(point => point.competitorKey === entry.competitorKey).length,
          activeTotal: activePoints.filter(point => point.competitorKey === entry.competitorKey).length,
          historicalTotal: historicalPoints.filter(point => point.competitorKey === entry.competitorKey).length,
          distribution: buildDistribution(points.filter(point => point.competitorKey === entry.competitorKey)),
        })),
        freshestPoint: sortedByFreshness[0] || null,
        stalestPoint: sortedByFreshness[sortedByFreshness.length - 1] || null,
      });
    });

    return map;
  }, [chartGroups, timeSeriesDataByGroup, competitorResults, competitorColors]);

  const renderLineTooltip = useCallback((value, name) => {
    if (String(name || '').endsWith('__util')) return `${percentFormatter.format(value)} %`;
    if (String(name || '').endsWith('__rev')) return formatCurrency(value);
    return value;
  }, []);

  const renderTimeSeriesTooltip = useCallback(
    ({ active, payload, label }) => {
      if (!active || !Array.isArray(payload) || !payload.length) return null;

      const row = payload[0]?.payload || {};
      const visibleEntityKeys = new Set(
        payload
          .map(item => String(item?.dataKey || '').replace(/__(util|rev)$/, ''))
          .filter(key => key && !key.startsWith('funzone_'))
      );
      const freshnessItems = (Array.isArray(row?.__freshnessItems) ? row.__freshnessItems : []).filter(item =>
        visibleEntityKeys.has(item.entityKey)
      );

      return (
        <Box sx={{ ...competitorTooltipBoxSx, p: 1.25, minWidth: 260 }}>
          <Typography variant='subtitle2' sx={{ color: 'text.primary', mb: 0.75 }}>
            {label} {row?.date ? `(${formatGermanDate(row.date)})` : ''}
          </Typography>

          <Stack spacing={0.5}>
            {payload.map(item => (
              <Stack key={`${item.dataKey}_${item.name}`} direction='row' justifyContent='space-between' spacing={2}>
                <Typography variant='caption' sx={{ color: item.color || 'text.secondary' }}>
                  {item.name}
                </Typography>
                <Typography variant='caption' sx={{ color: 'text.primary' }}>
                  {renderLineTooltip(item.value, item.dataKey)}
                </Typography>
              </Stack>
            ))}
          </Stack>

          {freshnessItems.length ? (
            <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
                Scrape-Zeitpunkte
              </Typography>
              <Stack spacing={0.35}>
                {freshnessItems.map(item => {
                  const historicalTarget = isHistoricalTargetDate(item.targetDate || row?.date);
                  return (
                    <Stack key={`${item.entityKey}_${item.scrapedAt}`} direction='row' justifyContent='space-between' spacing={2}>
                      <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                        {item.label}
                      </Typography>
                      <Typography variant='caption' sx={{ color: 'text.primary', textAlign: 'right' }}>
                        {historicalTarget
                          ? `${formatGermanDateTime(item.scrapedAt)} (historischer Zieldatensatz ${formatGermanDate(
                              item.targetDate || row?.date
                            )})`
                          : `${formatGermanDateTime(item.scrapedAt)} (${getRelativeTimeLabel(item.scrapedAt)})`}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
          ) : null}
        </Box>
      );
    },
    [renderLineTooltip, competitorTooltipBoxSx]
  );

  if (!authCtx.checkRight('competitorAvailability:read')) {
    return <Navigate to='/welcome' replace />;
  }

  const competitorsFilterNode = (
    <FormControl fullWidth>
      <InputLabel id='competitors-label'>Konkurrenten</InputLabel>
      <Select
        labelId='competitors-label'
        label='Konkurrenten'
        multiple
        value={selectedCompetitors}
        onChange={event => setSelectedCompetitors(event.target.value)}
        renderValue={selected =>
          selected
            .map(key => competitors.find(c => c.competitorKey === key)?.competitorName || key)
            .join(', ')
        }
      >
        {competitors.map(c => (
          <MenuItem key={c.competitorKey} value={c.competitorKey}>
            {c.competitorName || c.competitorKey}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const benchmarkFilterNode = (
    <FormControl fullWidth disabled={!selectedCompetitors.length || !benchmarkTypeKeysForSelection.length}>
      <InputLabel id='benchmark-entertainments-label'>FunZone-Entertainments</InputLabel>
      <Select
        labelId='benchmark-entertainments-label'
        label='FunZone-Entertainments'
        multiple
        value={selectedBenchmarkEntertainmentIds}
        onChange={event => {
          setDidManuallySelectBenchmarkEntertainmentIds(true);
          setSelectedBenchmarkEntertainmentIds(event.target.value);
        }}
        renderValue={selected =>
          (selected || [])
            .map(id => {
              const e = entertainmentsById.get(String(id));
              return e?.longName || e?.entertainmentName || String(id);
            })
            .join(', ')
        }
      >
        {benchmarkOptionsByType.length ? (
          benchmarkOptionsByType.flatMap(group => [
            <ListSubheader key={`h_${group.type}`}>{group.type}</ListSubheader>,
            ...(group.items || []).map(e => (
              <MenuItem key={e._id} value={String(e._id)}>
                {e.longName || e.entertainmentName}
              </MenuItem>
            )),
          ])
        ) : (
          <MenuItem disabled value='__empty__'>
            Keine passenden Entertainments gefunden
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );

  const applyExtraActionsNode = (
    <>
      <Button
        variant='outlined'
        onClick={downloadCsv}
        disabled={isFetchingStats || !appliedFilter?.competitors?.length}
        aria-label='CSV herunterladen'
        title='CSV herunterladen'
        sx={{ minWidth: 56, minHeight: 56, padding: 0, borderRadius: 2 }}
      >
        <FileDownloadIcon fontSize='small' />
      </Button>
      <Button
        variant='outlined'
        onClick={openBenchmarkDefaultsModal}
        aria-label='Benchmark-Zuordnung'
        title='Benchmark-Zuordnung'
        sx={{ minWidth: 56, minHeight: 56, padding: 0, borderRadius: 2 }}
      >
        <LinkIcon fontSize='small' />
      </Button>
    </>
  );

  const assumptionsPaperNode = (
    <Paper elevation={3} sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 }, p: 3, bgcolor: statSurfaceBg }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Typography variant='subtitle1' sx={{ mb: 1 }}>
            Standard: angenommene Spiele pro Spieler
          </Typography>
          <Stack direction='row' spacing={2} alignItems='center'>
            <Slider
              value={gamesPerPlayer}
              min={1}
              max={8}
              step={0.1}
              onChange={(_, v) => {
                const gp = Number(v);
                if (!Number.isFinite(gp)) return;
                setGamesPerPlayer(gp);
                applyGlobalAssumptionsToSelectedCompetitors({
                  nextGamesPerPlayer: gp,
                  nextRevenuePerPlayer: revenuePerPlayer,
                });
              }}
              valueLabelDisplay='auto'
            />
            <TextField
              size='small'
              value={gamesPerPlayerInput}
              onFocus={() => setIsGamesPerPlayerFocused(true)}
              onChange={e => {
                const next = e.target.value;
                setGamesPerPlayerInput(next);
                const parsed = clampNumber(next.replace(',', '.'), 1, 8);
                if (parsed === null) return;
                const gp = Number(parsed.toFixed(1));
                setGamesPerPlayer(gp);
                applyGlobalAssumptionsToSelectedCompetitors({
                  nextGamesPerPlayer: gp,
                  nextRevenuePerPlayer: revenuePerPlayer,
                });
              }}
              onBlur={() => {
                const parsed = clampNumber(gamesPerPlayerInput.replace(',', '.'), 1, 8);
                if (parsed === null) {
                  setGamesPerPlayerInput(String(Number(gamesPerPlayer).toFixed(1)));
                  setIsGamesPerPlayerFocused(false);
                  return;
                }
                const gp = Number(parsed.toFixed(1));
                setGamesPerPlayer(gp);
                applyGlobalAssumptionsToSelectedCompetitors({
                  nextGamesPerPlayer: gp,
                  nextRevenuePerPlayer: revenuePerPlayer,
                });
                setIsGamesPerPlayerFocused(false);
              }}
              inputProps={{ inputMode: 'decimal' }}
              sx={{ width: 110 }}
            />
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant='subtitle1' sx={{ mb: 1 }}>
            Standard: angenommener Umsatz pro Spieler
          </Typography>
          <Stack direction='row' spacing={2} alignItems='center'>
            <Slider
              value={revenuePerPlayer}
              min={0}
              max={80}
              step={0.5}
              onChange={(_, v) => {
                const rp = Number(v);
                if (!Number.isFinite(rp)) return;
                setRevenuePerPlayer(rp);
                applyGlobalAssumptionsToSelectedCompetitors({
                  nextGamesPerPlayer: gamesPerPlayer,
                  nextRevenuePerPlayer: rp,
                });
              }}
              valueLabelDisplay='auto'
            />
            <TextField
              size='small'
              value={revenuePerPlayerInput}
              onFocus={() => setIsRevenuePerPlayerFocused(true)}
              onChange={e => {
                const next = e.target.value;
                setRevenuePerPlayerInput(next);
                const parsed = clampNumber(next.replace(',', '.'), 0, 80);
                if (parsed === null) return;
                const rp = Number(parsed.toFixed(2));
                setRevenuePerPlayer(rp);
                applyGlobalAssumptionsToSelectedCompetitors({
                  nextGamesPerPlayer: gamesPerPlayer,
                  nextRevenuePerPlayer: rp,
                });
              }}
              onBlur={() => {
                const parsed = clampNumber(revenuePerPlayerInput.replace(',', '.'), 0, 80);
                if (parsed === null) {
                  setRevenuePerPlayerInput(String(Number(revenuePerPlayer).toFixed(2)));
                  setIsRevenuePerPlayerFocused(false);
                  return;
                }
                const rp = Number(parsed.toFixed(2));
                setRevenuePerPlayer(rp);
                applyGlobalAssumptionsToSelectedCompetitors({
                  nextGamesPerPlayer: gamesPerPlayer,
                  nextRevenuePerPlayer: rp,
                });
                setIsRevenuePerPlayerFocused(false);
              }}
              inputProps={{ inputMode: 'decimal' }}
              InputProps={{
                endAdornment: <InputAdornment position='end'>€</InputAdornment>,
              }}
              sx={{ width: 130 }}
            />
          </Stack>
        </Grid>

        {selectedCompetitors.length > 1 && (
          <Grid item xs={12}>
            <Accordion
              disableGutters
              elevation={0}
              sx={{ ...plainAccordionSx }}
              TransitionProps={{ unmountOnExit: true, mountOnEnter: true }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>
                  Pro Konkurrent (Standard-Werte überschreiben)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                  Overrides werden lokal angewendet und fließen nicht in den Cache-Key ein.
                </Typography>
                <Stack spacing={1.25}>
                  {selectedCompetitors
                    .map(k => String(k || '').trim())
                    .filter(Boolean)
                    .map(key => {
                      const c = competitorsByKey.get(key);
                      const name = c?.competitorName || key;
                      const city = c?.city ? ` (${c.city})` : '';
                      const entry = assumptionsByCompetitor?.[key] || {};
                      const gpInput = entry.gamesPerPlayerInput ?? String(Number(gamesPerPlayer).toFixed(1));
                      const rpInput = entry.revenuePerPlayerInput ?? String(Number(revenuePerPlayer).toFixed(2));

                      if (!competitorAssumptionDraftsRef.current[key]) {
                        competitorAssumptionDraftsRef.current[key] = {
                          gp: gpInput,
                          rp: rpInput,
                          editingGp: false,
                          editingRp: false,
                        };
                      } else {
                        if (!competitorAssumptionDraftsRef.current[key].editingGp) {
                          competitorAssumptionDraftsRef.current[key].gp = gpInput;
                        }
                        if (!competitorAssumptionDraftsRef.current[key].editingRp) {
                          competitorAssumptionDraftsRef.current[key].rp = rpInput;
                        }
                      }

                      return (
                        <Paper key={`assumption_${key}`} elevation={2} sx={{ p: 1.5, bgcolor: statAltSurfaceBg }}>
                          <Typography
                            variant='subtitle2'
                            lang='de'
                            sx={{
                              mb: 1,
                              color: 'text.secondary',
                              overflowWrap: 'anywhere',
                              wordBreak: 'break-word',
                              hyphens: 'auto',
                            }}
                          >
                            {addSoftHyphens(`${name}${city}`)}
                          </Typography>

                          <Grid container spacing={2} alignItems='center'>
                            <Grid item xs={12} md={6}>
                              <TextField
                                key={`assumption_gp_${key}_${gpInput}`}
                                fullWidth
                                size='small'
                                label='Spiele pro Spieler'
                                defaultValue={gpInput}
                                onFocus={() => {
                                  competitorAssumptionDraftsRef.current[key].editingGp = true;
                                }}
                                onChange={e => {
                                  competitorAssumptionDraftsRef.current[key].gp = e.target.value;
                                }}
                                onBlur={e => {
                                  const draft = competitorAssumptionDraftsRef.current[key]?.gp ?? e.target.value;
                                  const parsed = clampNumber(String(draft).replace(',', '.'), 1, 8);
                                  const finalVal =
                                    parsed === null
                                      ? Number(gamesPerPlayer)
                                      : Number(Number(parsed).toFixed(1));
                                  const normalized = String(finalVal.toFixed(1));
                                  competitorAssumptionDraftsRef.current[key].gp = normalized;
                                  competitorAssumptionDraftsRef.current[key].editingGp = false;
                                  setAssumptionsByCompetitor(prev => ({
                                    ...(prev || {}),
                                    [key]: {
                                      ...((prev || {})[key] || entry),
                                      gamesPerPlayer: finalVal,
                                      gamesPerPlayerInput: normalized,
                                      revenuePerPlayer: Number(((prev || {})[key] || entry)?.revenuePerPlayer ?? revenuePerPlayer),
                                      revenuePerPlayerInput: ((prev || {})[key] || entry)?.revenuePerPlayerInput ?? rpInput,
                                    },
                                  }));
                                }}
                                inputProps={{ inputMode: 'decimal' }}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                key={`assumption_rp_${key}_${rpInput}`}
                                fullWidth
                                size='small'
                                label='Umsatz pro Spieler'
                                defaultValue={rpInput}
                                onFocus={() => {
                                  competitorAssumptionDraftsRef.current[key].editingRp = true;
                                }}
                                onChange={e => {
                                  competitorAssumptionDraftsRef.current[key].rp = e.target.value;
                                }}
                                onBlur={e => {
                                  const draft = competitorAssumptionDraftsRef.current[key]?.rp ?? e.target.value;
                                  const parsed = clampNumber(String(draft).replace(',', '.'), 0, 80);
                                  const finalVal =
                                    parsed === null
                                      ? Number(revenuePerPlayer)
                                      : Number(Number(parsed).toFixed(2));
                                  const normalized = String(finalVal.toFixed(2));
                                  competitorAssumptionDraftsRef.current[key].rp = normalized;
                                  competitorAssumptionDraftsRef.current[key].editingRp = false;
                                  setAssumptionsByCompetitor(prev => ({
                                    ...(prev || {}),
                                    [key]: {
                                      ...((prev || {})[key] || entry),
                                      revenuePerPlayer: finalVal,
                                      revenuePerPlayerInput: normalized,
                                      gamesPerPlayer: Number(((prev || {})[key] || entry)?.gamesPerPlayer ?? gamesPerPlayer),
                                      gamesPerPlayerInput: ((prev || {})[key] || entry)?.gamesPerPlayerInput ?? gpInput,
                                    },
                                  }));
                                }}
                                inputProps={{ inputMode: 'decimal' }}
                                InputProps={{
                                  endAdornment: <InputAdornment position='end'>€</InputAdornment>,
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Paper>
                      );
                    })}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Grid>
        )}
      </Grid>
    </Paper>
  );

  const shellFilterParamConfig = useMemo(
    () => ({
      branchScope: { hidden: true },
      competitors: {
        label: 'Konkurrenten',
        format: value => {
          const count = String(value || '').split(',').map(s => s.trim()).filter(Boolean).length;
          if (!count) return null;
          return count === 1 ? '1 ausgewählt' : `${count} ausgewählt`;
        },
      },
      benchmarkEntertainmentIds: { hidden: true },
    }),
    []
  );

  return (
    <StatisticViewShell
      startDate={startDate}
      endDate={endDate}
      onStartDateChange={setStartDate}
      onEndDateChange={setEndDate}
      extraFilters={[competitorsFilterNode, benchmarkFilterNode]}
      onApply={applyFilters}
      applyDisabled={isFetchingStats}
      applyExtraActions={applyExtraActionsNode}
      status={jobStatus}
      isStale={isStale}
      cacheComputedAt={cacheComputedAt}
      cacheExpiresAt={cacheExpiresAt}
      job={job}
      error={jobError}
      cacheEntries={cacheEntries}
      selectedEntryId={selectedCacheEntryId}
      onSelectEntry={handleSelectCacheEntry}
      entriesLoading={entriesLoading}
      onRefresh={refreshStatistic}
      filterParamConfig={shellFilterParamConfig}
      hasPayload={!!statisticPayload}
    >
      {assumptionsPaperNode}

      <Box>
        <Dialog
          open={isBenchmarkDefaultsModalOpen}
          onClose={closeBenchmarkDefaultsModal}
          fullWidth
          maxWidth='sm'
          fullScreen={isSmallScreen}
        >
          <DialogTitle>Benchmark-Zuordnung</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel id='benchmark-defaults-competitor-label'>Konkurrent</InputLabel>
                <Select
                  labelId='benchmark-defaults-competitor-label'
                  label='Konkurrent'
                  value={benchmarkDefaultsCompetitorKey}
                  onChange={e => {
                    const key = String(e.target.value || '').trim();
                    setBenchmarkDefaultsCompetitorKey(key);
                    const c = key ? competitorsByKey.get(key) : null;
                    const ids = c
                      ? Array.from(
                          new Set(
                            [
                              ...(Array.isArray(c?.benchmarkEntertainmentIds) ? c.benchmarkEntertainmentIds : []),
                              ...(c?.entertainmentId ? [String(c.entertainmentId)] : []),
                            ]
                              .map(v => String(v || '').trim())
                              .filter(Boolean)
                          )
                        )
                      : [];
                    setBenchmarkDefaultsEntertainmentIds(ids);
                  }}
                >
                  {(competitors || []).map(c => (
                    <MenuItem key={c.competitorKey} value={c.competitorKey}>
                      {c.competitorName || c.competitorKey}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth disabled={!benchmarkDefaultsCompetitorKey}>
                <InputLabel id='benchmark-defaults-entertainments-label'>FunZone-Pendants</InputLabel>
                <Select
                  labelId='benchmark-defaults-entertainments-label'
                  label='FunZone-Pendants'
                  multiple
                  value={benchmarkDefaultsEntertainmentIds}
                  onChange={e => setBenchmarkDefaultsEntertainmentIds(e.target.value)}
                  renderValue={selected =>
                    (selected || [])
                      .map(id => {
                        const ent = entertainmentsById.get(String(id));
                        return ent?.longName || ent?.entertainmentName || String(id);
                      })
                      .join(', ')
                  }
                >
                  {benchmarkDefaultsEntertainmentOptions.length ? (
                    benchmarkDefaultsEntertainmentOptions.map(e => (
                      <MenuItem key={e._id} value={String(e._id)}>
                        {e.longName || e.entertainmentName}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled value='__empty__'>
                      Keine passenden Entertainments gefunden
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeBenchmarkDefaultsModal} disabled={isSavingBenchmarkDefaults}>
              Abbrechen
            </Button>
            <Button
              variant='contained'
              onClick={saveBenchmarkDefaults}
              disabled={isSavingBenchmarkDefaults}
            >
              Speichern
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

      <section>
        {cards.length ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem' }}>
            {cards.map(card => (
              <Box
                component='article'
                key={card.key}
                sx={{
                  bgcolor: statSurfaceBg,
                  borderRadius: 2,
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography component='strong' sx={{ fontSize: '1rem', fontWeight: 700 }}>
                  {card.title}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  {card.items.map(i => (
                    <Box key={i.label} sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      <Typography component='span' variant='body2' sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                        {i.label}
                      </Typography>
                      <Typography component='strong' sx={{ fontSize: '1.15rem', fontWeight: 700 }}>
                        {i.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </div>
        ) : (
          <p>Keine Daten für den ausgewählten Zeitraum vorhanden.</p>
        )}
      </section>

      <section>
        <h2 style={{ marginBottom: '0.5rem' }}>Auslastung & Umsatz (Zeitreihe)</h2>
        <>
            {chartGroups.map(group => (
              <Box key={group.key} sx={{ mb: 2 }}>
                <h3 style={{ margin: '0 0 0.75rem' }}>{group.title}</h3>
                <Grid container spacing={2}>
                  <Grid item xs={12} lg={6}>
                    <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg, height: 360 }}>
                      {timeSeriesDataByGroup.get(group.key)?.length ? (
                        <ResponsiveContainer width='100%' height='100%'>
                          <LineChart data={timeSeriesDataByGroup.get(group.key)}>
                            <CartesianGrid stroke={chartGridStroke} />
                            <XAxis
                              dataKey='label'
                              stroke={chartAxisStroke}
                              ticks={timeSeriesChartExtrasByGroup.get(group.key)?.ticks}
                              interval={0}
                              minTickGap={8}
                            />
                            <YAxis stroke={chartAxisStroke} domain={[0, 100]} />
                            <Tooltip content={renderTimeSeriesTooltip} />
                            <Legend />
                            {timeSeriesChartExtrasByGroup.get(group.key)?.futureX1 && (
                              <ReferenceArea
                                x1={timeSeriesChartExtrasByGroup.get(group.key).futureX1}
                                x2={timeSeriesChartExtrasByGroup.get(group.key).futureX2}
                                fill={chartRefAreaFill}
                                fillOpacity={0.06}
                                stroke={chartRefLineStroke}
                                strokeOpacity={0.4}
                                strokeDasharray='4 3'
                                ifOverflow='extendDomain'
                              />
                            )}
                            {timeSeriesChartExtrasByGroup.get(group.key)?.todayLabel && (
                              <ReferenceLine
                                x={timeSeriesChartExtrasByGroup.get(group.key).todayLabel}
                                stroke={chartRefLineStroke}
                                strokeDasharray='2 3'
                                label={{ value: 'heute', position: 'insideTopRight', fill: chartRefLabelFill, fontSize: 11 }}
                              />
                            )}
                            {competitorResults
                              .filter(c => group.competitorKeys.includes(c.competitorKey))
                              .map(c => (
                                <Line
                                  key={`${group.key}_${c.competitorKey}__util`}
                                  type='monotone' isAnimationActive={false}
                                  dataKey={`${c.competitorKey}__util`}
                                  name={`${c.competitorName || c.competitorKey} (Auslastung)`}
                                  stroke={competitorColors.get(c.competitorKey)}
                                  strokeWidth={2}
                                  dot={false}
                                  activeDot={{ r: 4 }}
                                />
                              ))}
                            {group.pendantIds.map(pid => {
                              const pendant = pendantsById.get(pid);
                              if (!pendant) return null;
                              const key = `funzone_${pid}`;
                              return (
                                <Line
                                  key={`${group.key}_${key}__util`}
                                  type='monotone' isAnimationActive={false}
                                  dataKey={`${key}__util`}
                                  name={`FunZone: ${pendant.entertainmentName} (Auslastung)`}
                                  stroke={pendantColors.get(pid)}
                                  strokeWidth={2}
                                  strokeDasharray='6 4'
                                  dot={false}
                                />
                              );
                            })}
                          </LineChart>
                        </ResponsiveContainer>
                      ) : (
                        <p>Keine Zeitreihendaten verfügbar.</p>
                      )}
                    </Paper>
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg, height: 360 }}>
                      {timeSeriesDataByGroup.get(group.key)?.length ? (
                        <ResponsiveContainer width='100%' height='100%'>
                          <LineChart data={timeSeriesDataByGroup.get(group.key)}>
                            <CartesianGrid stroke={chartGridStroke} />
                            <XAxis
                              dataKey='label'
                              stroke={chartAxisStroke}
                              ticks={timeSeriesChartExtrasByGroup.get(group.key)?.ticks}
                              interval={0}
                              minTickGap={8}
                            />
                            <YAxis stroke={chartAxisStroke} />
                            <Tooltip content={renderTimeSeriesTooltip} />
                            <Legend />
                            {timeSeriesChartExtrasByGroup.get(group.key)?.futureX1 && (
                              <ReferenceArea
                                x1={timeSeriesChartExtrasByGroup.get(group.key).futureX1}
                                x2={timeSeriesChartExtrasByGroup.get(group.key).futureX2}
                                fill={chartRefAreaFill}
                                fillOpacity={0.06}
                                stroke={chartRefLineStroke}
                                strokeOpacity={0.4}
                                strokeDasharray='4 3'
                                ifOverflow='extendDomain'
                              />
                            )}
                            {timeSeriesChartExtrasByGroup.get(group.key)?.todayLabel && (
                              <ReferenceLine
                                x={timeSeriesChartExtrasByGroup.get(group.key).todayLabel}
                                stroke={chartRefLineStroke}
                                strokeDasharray='2 3'
                                label={{ value: 'heute', position: 'insideTopRight', fill: chartRefLabelFill, fontSize: 11 }}
                              />
                            )}
                            {competitorResults
                              .filter(c => group.competitorKeys.includes(c.competitorKey))
                              .map(c => (
                                <Line
                                  key={`${group.key}_${c.competitorKey}__rev`}
                                  type='monotone' isAnimationActive={false}
                                  dataKey={`${c.competitorKey}__rev`}
                                  name={`${c.competitorName || c.competitorKey} (Umsatz)`}
                                  stroke={competitorColors.get(c.competitorKey)}
                                  strokeWidth={2}
                                  dot={false}
                                  activeDot={{ r: 4 }}
                                />
                              ))}
                            {group.pendantIds.map(pid => {
                              const pendant = pendantsById.get(pid);
                              if (!pendant) return null;
                              const key = `funzone_${pid}`;
                              return (
                                <Line
                                  key={`${group.key}_${key}__rev`}
                                  type='monotone' isAnimationActive={false}
                                  dataKey={`${key}__rev`}
                                  name={`FunZone: ${pendant.entertainmentName} (Umsatz)`}
                                  stroke={pendantColors.get(pid)}
                                  strokeWidth={2}
                                  strokeDasharray='6 4'
                                  dot={false}
                                />
                              );
                            })}
                          </LineChart>
                        </ResponsiveContainer>
                      ) : (
                        <p>Keine Zeitreihendaten verfügbar.</p>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            ))}
        </>
      </section>

      <section>
        <h2 style={{ marginBottom: '0.5rem' }}>Auslastung (Wochentag / Stunde)</h2>
        <>
            {chartGroups.map(group => (
              <Box key={`dist_${group.key}`} sx={{ mb: 2 }}>
                <h3 style={{ margin: '0 0 0.75rem' }}>{group.title}</h3>
                <Grid container spacing={2}>
                  <Grid item xs={12} lg={6}>
                    <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg, height: 320 }}>
                      {weekdayData.length ? (
                        <ResponsiveContainer width='100%' height='100%'>
                          <LineChart data={weekdayData}>
                            <CartesianGrid stroke={chartGridStroke} />
                            <XAxis dataKey='label' stroke={chartAxisStroke} />
                            <YAxis stroke={chartAxisStroke} domain={[0, 100]} />
                            <Tooltip contentStyle={chartTooltipContentStyle} formatter={value => `${percentFormatter.format(value)} %`} />
                            <Legend />
                            {competitorResults
                              .filter(c => group.competitorKeys.includes(c.competitorKey))
                              .map(c => (
                                <Line
                                  key={`weekday_${group.key}_${c.competitorKey}`}
                                  type='monotone' isAnimationActive={false}
                                  dataKey={c.competitorKey}
                                  name={c.competitorName || c.competitorKey}
                                  stroke={competitorColors.get(c.competitorKey)}
                                  strokeWidth={2}
                                  dot={false}
                                />
                              ))}
                            {group.pendantIds.map(pid => {
                              const pendant = pendantsById.get(pid);
                              if (!pendant) return null;
                              const key = `funzone_${pid}`;
                              return (
                                <Line
                                  key={`weekday_${group.key}_${key}`}
                                  type='monotone' isAnimationActive={false}
                                  dataKey={key}
                                  name={`FunZone: ${pendant.entertainmentName}`}
                                  stroke={pendantColors.get(pid)}
                                  strokeWidth={2}
                                  strokeDasharray='6 4'
                                  dot={false}
                                />
                              );
                            })}
                          </LineChart>
                        </ResponsiveContainer>
                      ) : (
                        <p>Keine Daten verfügbar.</p>
                      )}
                    </Paper>
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg, height: 320 }}>
                      {hourlyData.length ? (
                        <ResponsiveContainer width='100%' height='100%'>
                          <LineChart data={hourlyData}>
                            <CartesianGrid stroke={chartGridStroke} />
                            <XAxis dataKey='label' stroke={chartAxisStroke} interval={2} />
                            <YAxis stroke={chartAxisStroke} domain={[0, 100]} />
                            <Tooltip contentStyle={chartTooltipContentStyle} formatter={value => `${percentFormatter.format(value)} %`} />
                            <Legend />
                            {competitorResults
                              .filter(c => group.competitorKeys.includes(c.competitorKey))
                              .map(c => (
                                <Line
                                  key={`hourly_${group.key}_${c.competitorKey}`}
                                  type='monotone' isAnimationActive={false}
                                  dataKey={c.competitorKey}
                                  name={c.competitorName || c.competitorKey}
                                  stroke={competitorColors.get(c.competitorKey)}
                                  strokeWidth={2}
                                  dot={false}
                                />
                              ))}
                            {group.pendantIds.map(pid => {
                              const pendant = pendantsById.get(pid);
                              if (!pendant) return null;
                              const key = `funzone_${pid}`;
                              return (
                                <Line
                                  key={`hourly_${group.key}_${key}`}
                                  type='monotone' isAnimationActive={false}
                                  dataKey={key}
                                  name={`FunZone: ${pendant.entertainmentName}`}
                                  stroke={pendantColors.get(pid)}
                                  strokeWidth={2}
                                  strokeDasharray='6 4'
                                  dot={false}
                                />
                              );
                            })}
                          </LineChart>
                        </ResponsiveContainer>
                      ) : (
                        <p>Keine Daten verfügbar.</p>
                      )}
                    </Paper>
                  </Grid>
                </Grid>

                <Accordion
                  expanded={expandedWeekdayHourlyGroups.has(group.key)}
                  onChange={(_, expanded) => {
                    setExpandedWeekdayHourlyGroups(prev => {
                      const next = new Set(prev);
                      if (expanded) next.add(group.key);
                      else next.delete(group.key);
                      return next;
                    });
                  }}
                  sx={{ mt: 2, bgcolor: statSurfaceBg }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ color: 'text.primary' }}>
                    <Typography variant='subtitle1'>Stundenansicht je Wochentag</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {expandedWeekdayHourlyGroups.has(group.key) ? (
                      <Grid container spacing={2}>
                        {WEEKDAYS.map(d => (
                          <Grid key={`hourly_${group.key}_${d.key}`} item xs={12} md={6} xl={4}>
                            <Paper elevation={2} sx={{ p: 2, bgcolor: statAltSurfaceBg, height: 280 }}>
                              <Typography variant='subtitle2' sx={{ mb: 1, color: 'text.secondary' }}>
                                {d.label}
                              </Typography>
                              {hourlyDataByWeekday.get(d.key)?.length ? (
                                <ResponsiveContainer width='100%' height='100%'>
                                  <LineChart data={hourlyDataByWeekday.get(d.key)}>
                                    <CartesianGrid stroke={chartGridStroke} />
                                    <XAxis dataKey='label' stroke={chartAxisStroke} interval={2} />
                                    <YAxis stroke={chartAxisStroke} domain={[0, 100]} />
                                    <Tooltip
                                      contentStyle={chartTooltipContentStyle}
                                      formatter={value => `${percentFormatter.format(value)} %`}
                                    />
                                    <Legend />
                                    {competitorResults
                                      .filter(c => group.competitorKeys.includes(c.competitorKey))
                                      .map(c => (
                                        <Line
                                          key={`hourlyw_${d.key}_${group.key}_${c.competitorKey}`}
                                          type='monotone' isAnimationActive={false}
                                          dataKey={c.competitorKey}
                                          name={c.competitorName || c.competitorKey}
                                          stroke={competitorColors.get(c.competitorKey)}
                                          strokeWidth={2}
                                          dot={false}
                                        />
                                      ))}
                                    {group.pendantIds.map(pid => {
                                      const pendant = pendantsById.get(pid);
                                      if (!pendant) return null;
                                      const key = `funzone_${pid}`;
                                      return (
                                        <Line
                                          key={`hourlyw_${d.key}_${group.key}_${key}`}
                                          type='monotone' isAnimationActive={false}
                                          dataKey={key}
                                          name={`FunZone: ${pendant.entertainmentName}`}
                                          stroke={pendantColors.get(pid)}
                                          strokeWidth={2}
                                          strokeDasharray='6 4'
                                          dot={false}
                                        />
                                      );
                                    })}
                                  </LineChart>
                                </ResponsiveContainer>
                              ) : (
                                <p>Keine Daten verfügbar.</p>
                              )}
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    ) : null}
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  expanded={expandedRevenueDistGroups.has(group.key)}
                  onChange={(_, expanded) => {
                    setExpandedRevenueDistGroups(prev => {
                      const next = new Set(prev);
                      if (expanded) next.add(group.key);
                      else next.delete(group.key);
                      return next;
                    });
                  }}
                  sx={{ mt: 2, bgcolor: statSurfaceBg }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ color: 'text.primary' }}>
                    <Typography variant='subtitle1'>Umsatz (Wochentag / Stunde)</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {expandedRevenueDistGroups.has(group.key) ? (
                      <>
                        <Grid container spacing={2}>
                          <Grid item xs={12} lg={6}>
                            <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg, height: 320 }}>
                              {revenueWeekdayData.length ? (
                                <ResponsiveContainer width='100%' height='100%'>
                                  <LineChart data={revenueWeekdayData}>
                                    <CartesianGrid stroke={chartGridStroke} />
                                    <XAxis dataKey='label' stroke={chartAxisStroke} />
                                    <YAxis stroke={chartAxisStroke} />
                                    <Tooltip
                                      contentStyle={chartTooltipContentStyle}
                                      formatter={value => formatCurrency(value)}
                                    />
                                    <Legend />
                                    {competitorResults
                                      .filter(c => group.competitorKeys.includes(c.competitorKey))
                                      .map(c => (
                                        <Line
                                          key={`rev_weekday_${group.key}_${c.competitorKey}`}
                                          type='monotone' isAnimationActive={false}
                                          dataKey={c.competitorKey}
                                          name={c.competitorName || c.competitorKey}
                                          stroke={competitorColors.get(c.competitorKey)}
                                          strokeWidth={2}
                                          dot={false}
                                        />
                                      ))}
                                    {group.pendantIds.map(pid => {
                                      const pendant = pendantsById.get(pid);
                                      if (!pendant) return null;
                                      const key = `funzone_${pid}`;
                                      return (
                                        <Line
                                          key={`rev_weekday_${group.key}_${key}`}
                                          type='monotone' isAnimationActive={false}
                                          dataKey={key}
                                          name={`FunZone: ${pendant.entertainmentName}`}
                                          stroke={pendantColors.get(pid)}
                                          strokeWidth={2}
                                          strokeDasharray='6 4'
                                          dot={false}
                                        />
                                      );
                                    })}
                                  </LineChart>
                                </ResponsiveContainer>
                              ) : (
                                <p>Keine Daten verfügbar.</p>
                              )}
                            </Paper>
                          </Grid>
                          <Grid item xs={12} lg={6}>
                            <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg, height: 320 }}>
                              {revenueHourlyData.length ? (
                                <ResponsiveContainer width='100%' height='100%'>
                                  <LineChart data={revenueHourlyData}>
                                    <CartesianGrid stroke={chartGridStroke} />
                                    <XAxis dataKey='label' stroke={chartAxisStroke} interval={2} />
                                    <YAxis stroke={chartAxisStroke} />
                                    <Tooltip
                                      contentStyle={chartTooltipContentStyle}
                                      formatter={value => formatCurrency(value)}
                                    />
                                    <Legend />
                                    {competitorResults
                                      .filter(c => group.competitorKeys.includes(c.competitorKey))
                                      .map(c => (
                                        <Line
                                          key={`rev_hourly_${group.key}_${c.competitorKey}`}
                                          type='monotone' isAnimationActive={false}
                                          dataKey={c.competitorKey}
                                          name={c.competitorName || c.competitorKey}
                                          stroke={competitorColors.get(c.competitorKey)}
                                          strokeWidth={2}
                                          dot={false}
                                        />
                                      ))}
                                    {group.pendantIds.map(pid => {
                                      const pendant = pendantsById.get(pid);
                                      if (!pendant) return null;
                                      const key = `funzone_${pid}`;
                                      return (
                                        <Line
                                          key={`rev_hourly_${group.key}_${key}`}
                                          type='monotone' isAnimationActive={false}
                                          dataKey={key}
                                          name={`FunZone: ${pendant.entertainmentName}`}
                                          stroke={pendantColors.get(pid)}
                                          strokeWidth={2}
                                          strokeDasharray='6 4'
                                          dot={false}
                                        />
                                      );
                                    })}
                                  </LineChart>
                                </ResponsiveContainer>
                              ) : (
                                <p>Keine Daten verfügbar.</p>
                              )}
                            </Paper>
                          </Grid>
                        </Grid>

                        <Accordion
                          expanded={expandedRevenueWeekdayHourlyGroups.has(group.key)}
                          onChange={(_, expanded) => {
                            setExpandedRevenueWeekdayHourlyGroups(prev => {
                              const next = new Set(prev);
                              if (expanded) next.add(group.key);
                              else next.delete(group.key);
                              return next;
                            });
                          }}
                          sx={{ mt: 2, bgcolor: statAltSurfaceBg }}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ color: 'text.primary' }}>
                            <Typography variant='subtitle1'>Stundenansicht je Wochentag (Umsatz)</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            {expandedRevenueWeekdayHourlyGroups.has(group.key) ? (
                              <Grid container spacing={2}>
                                {WEEKDAYS.map(d => (
                                  <Grid key={`rev_hourly_${group.key}_${d.key}`} item xs={12} md={6} xl={4}>
                                    <Paper elevation={2} sx={{ p: 2, bgcolor: statDeepSurfaceBg, height: 280 }}>
                                      <Typography variant='subtitle2' sx={{ mb: 1, color: 'text.secondary' }}>
                                        {d.label}
                                      </Typography>
                                      {revenueHourlyDataByWeekday.get(d.key)?.length ? (
                                        <ResponsiveContainer width='100%' height='100%'>
                                          <LineChart data={revenueHourlyDataByWeekday.get(d.key)}>
                                            <CartesianGrid stroke={chartGridStroke} />
                                            <XAxis dataKey='label' stroke={chartAxisStroke} interval={2} />
                                            <YAxis stroke={chartAxisStroke} />
                                            <Tooltip
                                              contentStyle={chartTooltipContentStyle}
                                              formatter={value => formatCurrency(value)}
                                            />
                                            <Legend />
                                            {competitorResults
                                              .filter(c => group.competitorKeys.includes(c.competitorKey))
                                              .map(c => (
                                                <Line
                                                  key={`rev_hourlyw_${d.key}_${group.key}_${c.competitorKey}`}
                                                  type='monotone' isAnimationActive={false}
                                                  dataKey={c.competitorKey}
                                                  name={c.competitorName || c.competitorKey}
                                                  stroke={competitorColors.get(c.competitorKey)}
                                                  strokeWidth={2}
                                                  dot={false}
                                                />
                                              ))}
                                            {group.pendantIds.map(pid => {
                                              const pendant = pendantsById.get(pid);
                                              if (!pendant) return null;
                                              const key = `funzone_${pid}`;
                                              return (
                                                <Line
                                                  key={`rev_hourlyw_${d.key}_${group.key}_${key}`}
                                                  type='monotone' isAnimationActive={false}
                                                  dataKey={key}
                                                  name={`FunZone: ${pendant.entertainmentName}`}
                                                  stroke={pendantColors.get(pid)}
                                                  strokeWidth={2}
                                                  strokeDasharray='6 4'
                                                  dot={false}
                                                />
                                              );
                                            })}
                                          </LineChart>
                                        </ResponsiveContainer>
                                      ) : (
                                        <p>Keine Daten verfügbar.</p>
                                      )}
                                    </Paper>
                                  </Grid>
                                ))}
                              </Grid>
                            ) : null}
                          </AccordionDetails>
                        </Accordion>
                      </>
                    ) : null}
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  expanded={expandedGuestsDistGroups.has(group.key)}
                  onChange={(_, expanded) => {
                    setExpandedGuestsDistGroups(prev => {
                      const next = new Set(prev);
                      if (expanded) next.add(group.key);
                      else next.delete(group.key);
                      return next;
                    });
                  }}
                  sx={{ mt: 2, bgcolor: statSurfaceBg }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ color: 'text.primary' }}>
                    <Typography variant='subtitle1'>Gäste (Wochentag / Stunde)</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {expandedGuestsDistGroups.has(group.key) ? (
                      <>
                        <Grid container spacing={2}>
                          <Grid item xs={12} lg={6}>
                            <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg, height: 320 }}>
                              {guestsWeekdayData.length ? (
                                <ResponsiveContainer width='100%' height='100%'>
                                  <LineChart data={guestsWeekdayData}>
                                    <CartesianGrid stroke={chartGridStroke} />
                                    <XAxis dataKey='label' stroke={chartAxisStroke} />
                                    <YAxis stroke={chartAxisStroke} />
                                    <Tooltip
                                      contentStyle={chartTooltipContentStyle}
                                      formatter={value => `${Math.round(Number(value) || 0)}`}
                                    />
                                    <Legend />
                                    {competitorResults
                                      .filter(c => group.competitorKeys.includes(c.competitorKey))
                                      .map(c => (
                                        <Line
                                          key={`guest_weekday_${group.key}_${c.competitorKey}`}
                                          type='monotone' isAnimationActive={false}
                                          dataKey={c.competitorKey}
                                          name={c.competitorName || c.competitorKey}
                                          stroke={competitorColors.get(c.competitorKey)}
                                          strokeWidth={2}
                                          dot={false}
                                        />
                                      ))}
                                    {group.pendantIds.map(pid => {
                                      const pendant = pendantsById.get(pid);
                                      if (!pendant) return null;
                                      const key = `funzone_${pid}`;
                                      return (
                                        <Line
                                          key={`guest_weekday_${group.key}_${key}`}
                                          type='monotone' isAnimationActive={false}
                                          dataKey={key}
                                          name={`FunZone: ${pendant.entertainmentName}`}
                                          stroke={pendantColors.get(pid)}
                                          strokeWidth={2}
                                          strokeDasharray='6 4'
                                          dot={false}
                                        />
                                      );
                                    })}
                                  </LineChart>
                                </ResponsiveContainer>
                              ) : (
                                <p>Keine Daten verfügbar.</p>
                              )}
                            </Paper>
                          </Grid>
                          <Grid item xs={12} lg={6}>
                            <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg, height: 320 }}>
                              {guestsHourlyData.length ? (
                                <ResponsiveContainer width='100%' height='100%'>
                                  <LineChart data={guestsHourlyData}>
                                    <CartesianGrid stroke={chartGridStroke} />
                                    <XAxis dataKey='label' stroke={chartAxisStroke} interval={2} />
                                    <YAxis stroke={chartAxisStroke} />
                                    <Tooltip
                                      contentStyle={chartTooltipContentStyle}
                                      formatter={value => `${Math.round(Number(value) || 0)}`}
                                    />
                                    <Legend />
                                    {competitorResults
                                      .filter(c => group.competitorKeys.includes(c.competitorKey))
                                      .map(c => (
                                        <Line
                                          key={`guest_hourly_${group.key}_${c.competitorKey}`}
                                          type='monotone' isAnimationActive={false}
                                          dataKey={c.competitorKey}
                                          name={c.competitorName || c.competitorKey}
                                          stroke={competitorColors.get(c.competitorKey)}
                                          strokeWidth={2}
                                          dot={false}
                                        />
                                      ))}
                                    {group.pendantIds.map(pid => {
                                      const pendant = pendantsById.get(pid);
                                      if (!pendant) return null;
                                      const key = `funzone_${pid}`;
                                      return (
                                        <Line
                                          key={`guest_hourly_${group.key}_${key}`}
                                          type='monotone' isAnimationActive={false}
                                          dataKey={key}
                                          name={`FunZone: ${pendant.entertainmentName}`}
                                          stroke={pendantColors.get(pid)}
                                          strokeWidth={2}
                                          strokeDasharray='6 4'
                                          dot={false}
                                        />
                                      );
                                    })}
                                  </LineChart>
                                </ResponsiveContainer>
                              ) : (
                                <p>Keine Daten verfügbar.</p>
                              )}
                            </Paper>
                          </Grid>
                        </Grid>

                        <Accordion
                          expanded={expandedGuestsWeekdayHourlyGroups.has(group.key)}
                          onChange={(_, expanded) => {
                            setExpandedGuestsWeekdayHourlyGroups(prev => {
                              const next = new Set(prev);
                              if (expanded) next.add(group.key);
                              else next.delete(group.key);
                              return next;
                            });
                          }}
                          sx={{ mt: 2, bgcolor: statAltSurfaceBg }}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ color: 'text.primary' }}>
                            <Typography variant='subtitle1'>Stundenansicht je Wochentag (Gäste)</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            {expandedGuestsWeekdayHourlyGroups.has(group.key) ? (
                              <Grid container spacing={2}>
                                {WEEKDAYS.map(d => (
                                  <Grid key={`guest_hourly_${group.key}_${d.key}`} item xs={12} md={6} xl={4}>
                                    <Paper elevation={2} sx={{ p: 2, bgcolor: statDeepSurfaceBg, height: 280 }}>
                                      <Typography variant='subtitle2' sx={{ mb: 1, color: 'text.secondary' }}>
                                        {d.label}
                                      </Typography>
                                      {guestsHourlyDataByWeekday.get(d.key)?.length ? (
                                        <ResponsiveContainer width='100%' height='100%'>
                                          <LineChart data={guestsHourlyDataByWeekday.get(d.key)}>
                                            <CartesianGrid stroke={chartGridStroke} />
                                            <XAxis dataKey='label' stroke={chartAxisStroke} interval={2} />
                                            <YAxis stroke={chartAxisStroke} />
                                            <Tooltip
                                              contentStyle={chartTooltipContentStyle}
                                              formatter={value => `${Math.round(Number(value) || 0)}`}
                                            />
                                            <Legend />
                                            {competitorResults
                                              .filter(c => group.competitorKeys.includes(c.competitorKey))
                                              .map(c => (
                                                <Line
                                                  key={`guest_hourlyw_${d.key}_${group.key}_${c.competitorKey}`}
                                                  type='monotone' isAnimationActive={false}
                                                  dataKey={c.competitorKey}
                                                  name={c.competitorName || c.competitorKey}
                                                  stroke={competitorColors.get(c.competitorKey)}
                                                  strokeWidth={2}
                                                  dot={false}
                                                />
                                              ))}
                                            {group.pendantIds.map(pid => {
                                              const pendant = pendantsById.get(pid);
                                              if (!pendant) return null;
                                              const key = `funzone_${pid}`;
                                              return (
                                                <Line
                                                  key={`guest_hourlyw_${d.key}_${group.key}_${key}`}
                                                  type='monotone' isAnimationActive={false}
                                                  dataKey={key}
                                                  name={`FunZone: ${pendant.entertainmentName}`}
                                                  stroke={pendantColors.get(pid)}
                                                  strokeWidth={2}
                                                  strokeDasharray='6 4'
                                                  dot={false}
                                                />
                                              );
                                            })}
                                          </LineChart>
                                        </ResponsiveContainer>
                                      ) : (
                                        <p>Keine Daten verfügbar.</p>
                                      )}
                                    </Paper>
                                  </Grid>
                                ))}
                              </Grid>
                            ) : null}
                          </AccordionDetails>
                        </Accordion>
                      </>
                    ) : null}
                  </AccordionDetails>
                </Accordion>
              </Box>
            ))}
        </>
      </section>

      <section>
        <h2 style={{ marginBottom: '0.5rem' }}>Auslastung (Zeitstrahl)</h2>
        <>
            {chartGroups.map(group => (
              <Box key={`timeline_${group.key}`} sx={{ mb: 2, mt: 2 }}>
                <h3 style={{ margin: '0 0 0.75rem' }}>{group.title}</h3>
                <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg }}>
                  <Stack spacing={2}>
                    {competitorResults
                      .filter(c => group.competitorKeys.includes(c.competitorKey))
                      .map(c => {
                        const title = `${c.competitorName || c.competitorKey}${c.city ? ` (${c.city})` : ''}`;
                        const multiCompetitorMode = (appliedFilter?.competitors || []).length > 1;
                        const perC = assumptionsByCompetitor?.[c.competitorKey];
                        const gp = multiCompetitorMode ? Number(perC?.gamesPerPlayer ?? gamesPerPlayer) : gamesPerPlayer;
                        const distRaw =
                          (c?.distributions?.halfHourly && c.distributions.halfHourly.length)
                            ? c.distributions.halfHourly
                            : c?.distributions?.hourly;
                        const distWithPeople = (Array.isArray(distRaw) ? distRaw : []).map(h => ({
                          ...(h || {}),
                          absoluteCount: gp > 0 ? Number(h?.bookedSlots || 0) / gp : 0,
                        }));
                        const timeline = buildTimelineDataFromHourlyDistribution(title, distWithPeople);
                        return (
                          <Box key={`timeline_comp_${group.key}_${c.competitorKey}`}>
                            <Typography
                              variant='subtitle2'
                              lang='de'
                              sx={{
                                mb: 0.75,
                                color: 'text.secondary',
                                overflowWrap: 'anywhere',
                                wordBreak: 'break-word',
                                hyphens: 'auto',
                              }}
                            >
                              {addSoftHyphens(title)}
                            </Typography>
                            <EntertainmentCapacity data={timeline} condensedMode showAbsoluteCountInHeader showTotalColumn />
                          </Box>
                        );
                      })}
                    {group.pendantIds.map(pid => {
                      const pendant = pendantsById.get(pid);
                      if (!pendant) return null;
                      const title = `FunZone: ${pendant.entertainmentName}`;
                      const distRaw =
                        (pendant?.distributions?.halfHourly && pendant.distributions.halfHourly.length)
                          ? pendant.distributions.halfHourly
                          : pendant?.distributions?.hourly;
                      const distWithPeople = (Array.isArray(distRaw) ? distRaw : []).map(h => ({
                        ...(h || {}),
                        absoluteCount: Number(h?.participantCount || 0),
                      }));
                      const timeline = buildTimelineDataFromHourlyDistribution(title, distWithPeople);
                      return (
                        <Box key={`timeline_funzone_${group.key}_${pid}`}>
                          <Typography
                            variant='subtitle2'
                            lang='de'
                            sx={{
                              mb: 0.75,
                              color: 'text.secondary',
                              overflowWrap: 'anywhere',
                              wordBreak: 'break-word',
                              hyphens: 'auto',
                            }}
                          >
                            {addSoftHyphens(title)}
                          </Typography>
                          <EntertainmentCapacity data={timeline} condensedMode showAbsoluteCountInHeader showTotalColumn />
                        </Box>
                      );
                    })}
                  </Stack>
                </Paper>

                <Accordion
                  expanded={expandedWeekdayTimelineGroups.has(group.key)}
                  onChange={(_, expanded) => {
                    setExpandedWeekdayTimelineGroups(prev => {
                      const next = new Set(prev);
                      if (expanded) next.add(group.key);
                      else next.delete(group.key);
                      return next;
                    });
                  }}
                  sx={{ mt: 2, bgcolor: statSurfaceBg }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ color: 'text.primary' }}>
                    <Typography variant='subtitle1'>Zeitstrahl je Wochentag</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {expandedWeekdayTimelineGroups.has(group.key) ? (
                      <Stack spacing={2}>
                        {WEEKDAYS.map(d => (
                          <Paper key={`timeline_weekday_${group.key}_${d.key}`} elevation={2} sx={{ p: 2, bgcolor: statAltSurfaceBg }}>
                            <Typography variant='subtitle2' sx={{ mb: 0.75, color: 'text.secondary' }}>
                              {d.label}
                            </Typography>
                            <Stack spacing={2}>
                              {competitorResults
                                .filter(c => group.competitorKeys.includes(c.competitorKey))
                                .map(c => {
                                  const title = `${c.competitorName || c.competitorKey}${c.city ? ` (${c.city})` : ''}`;
                                  const multiCompetitorMode = (appliedFilter?.competitors || []).length > 1;
                                  const perC = assumptionsByCompetitor?.[c.competitorKey];
                                  const gp = multiCompetitorMode ? Number(perC?.gamesPerPlayer ?? gamesPerPlayer) : gamesPerPlayer;
                                  const halfHourly =
                                    (c?.distributions?.halfHourlyByWeekday || []).find(w => w?.weekday === d.key)?.halfHourly ||
                                    [];
                                  const hourly =
                                    (c?.distributions?.hourlyByWeekday || []).find(w => w?.weekday === d.key)?.hourly || [];
                                  const distWithPeople = (halfHourly.length ? halfHourly : hourly).map(h => ({
                                    ...(h || {}),
                                    absoluteCount: gp > 0 ? Number(h?.bookedSlots || 0) / gp : 0,
                                  }));
                                  const weekdayBookedSlots = Number(
                                    (c?.distributions?.weekday || []).find(w => w?.weekday === d.key)?.bookedSlots || 0
                                  );
                                  const totalPeople = gp > 0 ? weekdayBookedSlots / gp : 0;
                                  const timeline = buildTimelineDataFromHourlyDistribution(title, distWithPeople, totalPeople);
                                  return (
                                    <Box key={`timelinew_comp_${group.key}_${d.key}_${c.competitorKey}`}>
                                      <Typography
                                        variant='subtitle2'
                                        lang='de'
                                        sx={{
                                          mb: 0.75,
                                          color: 'text.secondary',
                                          overflowWrap: 'anywhere',
                                          wordBreak: 'break-word',
                                          hyphens: 'auto',
                                        }}
                                      >
                                        {addSoftHyphens(title)}
                                      </Typography>
                                      <EntertainmentCapacity data={timeline} condensedMode showAbsoluteCountInHeader showTotalColumn />
                                    </Box>
                                  );
                                })}
                              {group.pendantIds.map(pid => {
                                const pendant = pendantsById.get(pid);
                                if (!pendant) return null;
                                const title = `FunZone: ${pendant.entertainmentName}`;
                                const halfHourly =
                                  (pendant?.distributions?.halfHourlyByWeekday || []).find(w => w?.weekday === d.key)?.halfHourly ||
                                  [];
                                const hourly =
                                  (pendant?.distributions?.hourlyByWeekday || []).find(w => w?.weekday === d.key)?.hourly ||
                                  [];
                                const distWithPeople = (halfHourly.length ? halfHourly : hourly).map(h => ({
                                  ...(h || {}),
                                  absoluteCount: Number(h?.participantCount || 0),
                                }));
                                const weekdayParticipantCount = Number(
                                  (pendant?.distributions?.weekday || []).find(w => w?.weekday === d.key)?.participantCount ||
                                    0
                                );
                                const fallbackSum = distWithPeople
                                  .filter(x => String(x?.label || x?.time || '').endsWith(':00'))
                                  .reduce((acc, x) => acc + (Number(x?.absoluteCount) || 0), 0);
                                const totalPeople = weekdayParticipantCount > 0 ? weekdayParticipantCount : fallbackSum;
                                const timeline = buildTimelineDataFromHourlyDistribution(title, distWithPeople, totalPeople);
                                return (
                                  <Box key={`timelinew_funzone_${group.key}_${d.key}_${pid}`}>
                                    <Typography
                                      variant='subtitle2'
                                      lang='de'
                                      sx={{
                                        mb: 0.75,
                                        color: 'text.secondary',
                                        overflowWrap: 'anywhere',
                                        wordBreak: 'break-word',
                                        hyphens: 'auto',
                                      }}
                                    >
                                      {addSoftHyphens(title)}
                                    </Typography>
                                    <EntertainmentCapacity data={timeline} condensedMode showAbsoluteCountInHeader showTotalColumn />
                                  </Box>
                                );
                              })}
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    ) : null}
                  </AccordionDetails>
                </Accordion>
              </Box>
            ))}
        </>
      </section>

      <section>
        <h2 style={{ marginBottom: '0.5rem' }}>Aktualität der Wettbewerbsdaten</h2>
        <>
            {chartGroups.map(group => {
              const freshnessSummary = freshnessSummaryByGroup.get(group.key) || {};
              const points = Array.isArray(freshnessSummary.points) ? freshnessSummary.points : [];
              const activePoints = Array.isArray(freshnessSummary.activePoints) ? freshnessSummary.activePoints : [];
              const historicalPoints = Array.isArray(freshnessSummary.historicalPoints)
                ? freshnessSummary.historicalPoints
                : [];
              const overallDistribution = Array.isArray(freshnessSummary.overallDistribution)
                ? freshnessSummary.overallDistribution
                : [];
              const perCompetitorDistribution = Array.isArray(freshnessSummary.perCompetitorDistribution)
                ? freshnessSummary.perCompetitorDistribution
                : [];
              const freshestPoint = freshnessSummary.freshestPoint || null;
              const stalestPoint = freshnessSummary.stalestPoint || null;

              return (
                <Box key={`freshness_${group.key}`} sx={{ mb: 2 }}>
                  <h3 style={{ margin: '0 0 0.75rem' }}>{group.title}</h3>
                  {points.length ? (
                    <Stack spacing={2}>
                      <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg }}>
                        <Stack direction='row' spacing={1} useFlexGap flexWrap='wrap'>
                          <Chip variant='outlined' label={`Scrape-Punkte: ${points.length}`} />
                          <Chip variant='outlined' color='info' label={`Historische Zieltage: ${historicalPoints.length}`} />
                          <Chip variant='outlined' label={`Offene Zieltage: ${activePoints.length}`} />
                          {freshestPoint ? (
                            <Chip
                              color={buildFreshnessMeta(theme, freshestPoint.scrapedAt).chipColor}
                              label={`Frischster offener Datenpunkt: ${formatAgeLabel(freshestPoint.ageHours)}`}
                            />
                          ) : null}
                          {stalestPoint ? (
                            <Chip variant='outlined' label={`Ältester offener Datenpunkt: ${formatAgeLabel(stalestPoint.ageHours)}`} />
                          ) : null}
                          {!activePoints.length && historicalPoints.length ? (
                            <Chip color='info' variant='outlined' label='Im Zeitraum liegen nur historische Zieltage vor.' />
                          ) : null}
                        </Stack>
                      </Paper>

                      <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg }}>
                        <Typography variant='subtitle1' sx={{ mb: 1 }}>
                          Gesamtverteilung
                        </Typography>
                        <Stack spacing={0.75}>
                          {overallDistribution
                            .filter(entry => entry.count > 0)
                            .map(entry => (
                              <Stack
                                key={`overall_${group.key}_${entry.key}`}
                                direction='row'
                                justifyContent='space-between'
                                spacing={2}
                                sx={{ borderBottom: 1, borderColor: 'divider', pb: 0.5 }}
                              >
                                <Typography variant='body2'>{entry.label}</Typography>
                                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                                  {entry.percent.toFixed(1)} % ({entry.count}{' '}
                                  {entry.count === 1 ? 'Datenpunkt' : 'Datenpunkte'})
                                </Typography>
                              </Stack>
                            ))}
                        </Stack>
                      </Paper>

                      <Grid container spacing={2}>
                        {perCompetitorDistribution.map(entry => (
                          <Grid item xs={12} md={6} xl={4} key={`freshness_comp_${group.key}_${entry.competitorKey}`}>
                            <Paper
                              elevation={3}
                              sx={{
                                p: 2,
                                height: '100%',
                                bgcolor: statSurfaceBg,
                                borderTop: `3px solid ${entry.color}`,
                              }}
                            >
                              <Typography variant='subtitle1' sx={{ mb: 0.25 }}>
                                {entry.label}
                              </Typography>
                              <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary', mb: 1.25 }}>
                                {entry.total} Scrape-Punkte · {entry.historicalTotal} historisch · {entry.activeTotal} offen
                              </Typography>
                              <Stack spacing={0.6}>
                                {entry.distribution
                                  .filter(bucket => bucket.count > 0)
                                  .map(bucket => (
                                    <Stack
                                      key={`freshness_bucket_${group.key}_${entry.competitorKey}_${bucket.key}`}
                                      direction='row'
                                      justifyContent='space-between'
                                      spacing={2}
                                    >
                                      <Typography variant='body2'>{bucket.label}</Typography>
                                      <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                                        {bucket.percent.toFixed(1)} %
                                      </Typography>
                                    </Stack>
                                  ))}
                              </Stack>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </Stack>
                  ) : (
                    <p>Keine Scrape-Zeitpunkte für den ausgewählten Zeitraum verfügbar.</p>
                  )}
                </Box>
              );
            })}
        </>
      </section>

    </StatisticViewShell>
  );
};

export default CompetitorAvailability;

